import {
  Fragment, useCallback, useEffect, useMemo, useRef, useState,
  type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CalendarDays, ChevronLeft, ChevronRight, ChevronDown, X,
  Clock, Loader2, ExternalLink, Volume2, Star, AlertCircle, DollarSign,
  Maximize2, Minimize2, ShieldCheck, Search, DoorOpen, Check, Sun,
  Droplets, FileText, Pin, ArrowRight, Inbox, UserPlus, UserCheck,
  MessageSquare, Activity, Eye, MapPin,
} from 'lucide-react';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/services/firebase/config';
import { useClinic } from '@/stores/ClinicContext';
import { useAuth } from '@/hooks/useAuth';
import { useAgendaUnit } from '@/hooks/useAgendaUnit';
import { usePendencias } from '@/hooks/usePendencias';
import { AppointmentService, normalizeAppointment } from '@/services/firebase/appointments';
import { AgendaConfigService } from '@/services/firebase/agendaConfig';
import { DayRangesStrip } from '@/components/agenda/DayRangesStrip';
import {
  type BlockedRange, parseBlockedDates, parseReleases, type ReleasedRange, reservedRanges,
} from '@/utils/blockedDates';
import { QueueService } from '@/services/firebase/queue';
import { useNotifyDoctor } from '@/hooks/useNotifyDoctor';
import { PatientService } from '@/services/firebase/patients';
import { NextUpAccompanistModal } from '@/components/patients/NextUpAccompanistModal';
import { isInsideModalPortal } from '@/components/ui/Modal';
import { pickNextUp } from '@/utils/nextUp';
import { normalizeForSearch } from '@/utils/crypto';
import { calculateAgeFromBirthDate } from '@/utils/birthDate';
import {
  hasProcedureDetail, procedureNames,
  hasChargeRecord, amountReceived, remainingBalance, hasOutstandingBalance,
  fmtMoneyBRL, PAYMENT_STATUS_LABEL, PAYMENT_STATUS_TONE, isZeroCharge,
} from '@/utils/appointmentCharge';
import { useAgendaDock, type DockView } from './AgendaDockContext';
import { releaseClickFocus, useCockpitSanfonaEnabled } from './useSanfona';
import { GlobalPatientSearch } from './GlobalPatientSearch';
import { useVoiceDockPx } from '@/components/voice/voiceDockWidth';
import { Role } from '@/types/auth';
import type { Appointment, AgendaConfigDay, QueueEntry } from '@/types/models';

/**
 * Gaveta retrátil global com a AGENDA DO DIA. Vive no Shell (igual ao
 * FloatingChat), então abre por cima de QUALQUER tela — ficha do paciente,
 * dashboard, prescrições — sem trocar de rota. O médico dá uma espiada no
 * próximo paciente e fecha, continuando exatamente onde estava.
 *
 * Abertura: trilho fixo na borda direita + atalho de teclado (Alt+A).
 * Fechamento: clique no trilho/alça, botão X, ESC ou clique fora do painel.
 *
 * Espelha a "day view" da AgendaPage: mostra o dia inteiro da clínica
 * (sem filtro por profissional), ordenado por horário de início.
 *
 * ── Leitura da gaveta (redesenho ago/2026) ────────────────────────────
 * A ordem visual segue a ordem de urgência do consultório, e não a ordem
 * cronológica bruta:
 *   1. AGORA      — quem está em atendimento, com cronômetro correndo;
 *   2. PRÓXIMO    — quem entra em seguida;
 *   3. SALA DE ESPERA — quem chegou, com o tempo de espera explícito por
 *      pessoa e cor codificando o atraso contra a meta da clínica;
 *   4. RESTO DO DIA e ENCERRADOS (recolhível).
 * Nada foi removido: todo agendamento aparece exatamente uma vez, e os
 * selos (convênio, cobrança, urgência, dilatação…) continuam nos cards.
 */

// Combinação que abre/fecha a gaveta: Alt + esta tecla física (e.code).
// Usar e.code deixa o atalho independente do layout do teclado.
const TOGGLE_CODE = 'KeyA';

/** Mesma chave da QueuePage / PatientQuickPanel — sala persiste por médico. */
const ROOM_STORAGE_KEY = (uid: string) => `dratento.queue.room.${uid}`;

/** "Manter sempre aberta": persiste a escolha por navegador. Quando ligada, a
 *  agenda abre sozinha e NÃO fecha ao clicar fora (igual ao "sempre aberto" do
 *  chat). */
const ALWAYS_OPEN_STORAGE_KEY = 'dratento-agenda-always-open';

/** Conteúdo mínimo (px) que precisa sobrar pra agenda ENCAIXAR ao lado em vez
 *  de cobrir o sistema. Se a janela não comporta painel + esse mínimo (telas
 *  estreitas ou modo ampliado apertado), a gaveta volta a abrir por cima. */
const DOCK_MIN_CONTENT = 360;

/** Larguras (px) dos dois níveis de abertura do painel. O ampliado revela a
 *  agenda do dia inteira — em 2 colunas, sem truncar nomes/procedimentos e
 *  com observações. Ambos são capados em 94vw pra nunca estourar a tela. */
const NORMAL_WIDTH = 388;
const WIDE_WIDTH = 880;

/** Trilho de ícones colado na borda direita (sempre visível no desktop):
 *  Agenda · Chat · Resumo · Optotipos + os mostradores vivos do dia. O
 *  conteúdo reserva esta largura, então o trilho nunca cobre nada. */
const RAIL_WIDTH = 52;

/** Sanfona (ver cockpitSanfona): quanto o mouse precisa PARAR no trilho pra
 *  agenda espiar, e quanto ela espera depois que o mouse sai pra recolher. */
const PEEK_OPEN_MS = 200;
const PEEK_CLOSE_MS = 380;

/** Largura do painel por vista. A agenda tem dois níveis (parcial/ampliado);
 *  as demais abrem num tamanho fixo. */
const VIEW_WIDTH: Record<Exclude<DockView, 'agenda'>, number> = {
  chat: NORMAL_WIDTH,
  resumo: NORMAL_WIDTH,
  optotipos: 320,
  paciente: 380,
};

const VIEW_LABEL: Record<DockView, string> = {
  agenda: 'Agenda do dia',
  chat: 'Chat',
  resumo: 'Resumo clínico',
  optotipos: 'Optotipos',
  paciente: 'Paciente',
};

/** Meta de espera da clínica, em minutos. É o corte que pinta a espera de
 *  terracota na sala de espera e a frase que interpreta o número ("passou
 *  dos 15 min de meta da clínica"). */
const WAIT_TARGET_MIN = 15;

/** Corte intermediário: acima disto a espera já sai do verde e vira âmbar. */
const WAIT_WARM_MIN = 8;

/** Janela (dias) que o estado vazio varre pra montar "o que vem" e
 *  "poderiam vir hoje". Só é consultada quando o dia está sem agenda. */
const UPCOMING_DAYS = 14;

/** Quantos caracteres do termo digitado entram no eco da busca. O resto vira
 *  reticências — é o que impede um nome completo de esticar a gaveta. */
const TERM_ECHO_MAX = 24;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const endOfDay = (d: Date) => startOfDay(d) + 24 * 60 * 60 * 1000 - 1;

const STATUS_LABEL: Record<Appointment['status'], string> = {
  PENDENTE: 'Pendente',
  AGENDADA: 'Agendada',
  CONFIRMADA: 'Confirmada',
  EM_ESPERA: 'Em espera',
  EM_ATENDIMENTO: 'Em atendimento',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
  FALTOU: 'Faltou',
};

const STATUS_TONE: Record<Appointment['status'], string> = {
  PENDENTE: 'bg-warning/15 text-warning',
  AGENDADA: 'bg-primary/15 text-primary',
  CONFIRMADA: 'bg-sage/15 text-sage',
  EM_ESPERA: 'bg-warning/15 text-warning',
  EM_ATENDIMENTO: 'bg-live/15 text-live',
  CONCLUIDA: 'bg-on-surface/10 text-on-surface-variant',
  CANCELADA: 'bg-on-surface/10 text-on-surface-variant',
  FALTOU: 'bg-error/15 text-error',
};

/**
 * Cor da barrinha de 3px na ponta esquerda de cada linha — a "espinha" que
 * dá a leitura do dia sem precisar ler texto. Encerrados perdem a cor; os
 * vivos herdam a cor da natureza da visita (consulta = sage, procedimento =
 * terracota, retorno = neutro), como no mockup validado.
 */
const rowBarTone = (a: Appointment): string => {
  if (a.status === 'EM_ATENDIMENTO') return 'bg-live';
  if (a.status === 'CONCLUIDA') return 'bg-sage/45';
  if (a.status === 'FALTOU') return 'bg-error/50';
  if (a.status === 'CANCELADA') return 'bg-on-surface/20';
  if (a.type === 'CONSULTA' || a.type === 'PRIMEIRA_VEZ') return 'bg-sage';
  if (a.type === 'PROCEDIMENTO' || a.type === 'EXAME') return 'bg-terracotta';
  return 'bg-on-surface-variant/50';
};

/** Soma `minutes` a um horário "HH:MM" (trava em 23:59). Usado pra datar
 *  cada procedimento da visita (início + i × duração do slot). */
const addMinutesToHM = (hm: string, minutes: number): string => {
  const [h, m] = hm.split(':').map(Number);
  const total = Math.min((h || 0) * 60 + (m || 0) + minutes, 23 * 60 + 59);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

/** "HH:MM" → minutos desde a meia-noite. */
const hmToMinutes = (hm: string): number => {
  const [h, m] = hm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** Minutagem de espera: "5min", "47min", "1h05". */
const formatWait = (ms: number): string => {
  const totalMin = Math.max(0, Math.floor(ms / 60_000));
  if (totalMin < 60) return `${totalMin}min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h${String(m).padStart(2, '0')}`;
};

/** Minutos inteiros de espera — o número que a sala de espera mostra e que
 *  decide a cor do ponto. */
const waitMinutes = (ms: number): number => Math.max(0, Math.floor(ms / 60_000));

/** Rótulo curto de um horário "HH:MM": "13h" quando cheio, senão "13:30". */
const shiftLabel = (hm: string): string => {
  const [h, m] = hm.split(':');
  return m === '00' ? `${parseInt(h, 10)}h` : `${h}:${m}`;
};

/** Relógio "HH:MM" de um epoch ms. */
const clockOf = (ms: number): string =>
  new Date(ms).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

/** Primeiro nome — o suficiente pra frase que interpreta a espera. */
const firstName = (full?: string): string => (full || 'Paciente').trim().split(/\s+/)[0];

/** Eco do termo digitado: entra encurtado, nunca estica a gaveta. */
const shortTerm = (s: string): string =>
  s.length > TERM_ECHO_MAX ? `${s.slice(0, TERM_ECHO_MAX)}…` : s;

/** Normalização só pra casar posição de destaque (sem `trim`, que
 *  deslocaria os índices em relação ao texto original). */
const normLoose = (s: string): string =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/** Chave YYYY-MM-DD de um epoch ms (fuso local). */
const dateKey = (ms: number): string => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Cronômetro vivo do atendimento em curso (HH:MM:SS). Tem o PRÓPRIO
 * intervalo de 1s de propósito: o segundo corre sem re-renderizar a gaveta
 * inteira a cada tique (a lista do dia continua no tique de 30s do painel).
 */
const Chrono = ({ startedAt, className }: { startedAt: number; className?: string }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const total = Math.max(0, Math.floor((now - startedAt) / 1000));
  const hh = String(Math.floor(total / 3600)).padStart(2, '0');
  const mm = String(Math.floor(total / 60) % 60).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return <span className={className}>{`${hh}:${mm}:${ss}`}</span>;
};

/** Destaca o trecho casado do nome durante a busca (acento-insensível). */
const Highlight = ({ text, term }: { text: string; term: string }) => {
  if (!term) return <>{text}</>;
  const i = normLoose(text).indexOf(normLoose(term));
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark
        className="rounded-sm bg-amber/20 px-px text-inherit"
        style={{ boxShadow: 'inset 0 -1.5px 0 rgb(var(--c-amber) / 0.45)' }}
      >
        {text.slice(i, i + term.length)}
      </mark>
      {text.slice(i + term.length)}
    </>
  );
};

/** Cabeçalho de seção: rótulo mono + contagem + fio até a borda. */
const Sec = ({ label, count, pad = 'px-4' }: { label: string; count?: number; pad?: string }) => (
  <div className={`flex items-center gap-2.5 pt-4 pb-1.5 ${pad}`}>
    <span className="label-mono shrink-0">{label}</span>
    {count != null && (
      <span className="num-mono shrink-0 text-[10px] text-on-surface-variant/70">
        {String(count).padStart(2, '0')}
      </span>
    )}
    <span className="h-px flex-1 bg-outline/10" />
  </div>
);

/**
 * Pendências que NÃO dependem da agenda — o bloco que impede o dia vazio de
 * virar um beco. Vive num componente à parte de propósito: `usePendencias`
 * abre listeners e faz várias leituras, então só monta quando o dia está
 * realmente sem agendamento (e a gaveta, aberta). O filtro por papel já vem
 * de dentro do hook.
 */
const EmptyDayPendencias = ({
  clinicId,
  role,
  onOpen,
}: {
  clinicId: string;
  role: Parameters<typeof usePendencias>[1];
  onOpen: () => void;
}) => {
  const p = usePendencias(clinicId, role);
  const items = [
    { key: 'ap', label: 'Agendamentos aguardando aprovação', hint: 'Pedidos do portal e do agendador', n: p.appointments.length, tone: 'bg-warning/15 text-warning' },
    { key: 'ov', label: 'Procedimentos solicitados em atraso', hint: 'Passaram do prazo configurado', n: p.overdueProcedures.length, tone: 'bg-terracotta/15 text-terracotta' },
    { key: 'br', label: 'Pacientes aguardando orientação', hint: 'Atendimento finalizado, orientação pendente', n: p.briefingsNeeded.length, tone: 'bg-primary/15 text-primary' },
    { key: 'pc', label: 'Cancelamentos feitos pelo portal', hint: 'Vaga liberada sem ninguém saber', n: p.portalCancellations.length, tone: 'bg-terracotta/15 text-terracotta' },
    { key: 'np', label: 'Cadastros novos do portal', hint: 'Aguardando conferência', n: p.newPortalPatients.length, tone: 'bg-primary/15 text-primary' },
    { key: 'lr', label: 'Solicitações de lente do portal', hint: 'Aguardando avaliação', n: p.lensRequests.length, tone: 'bg-sage/15 text-sage' },
    { key: 'st', label: 'Estoque abaixo do mínimo', hint: 'Reposição', n: p.stockAlerts.length, tone: 'bg-warning/15 text-warning' },
  ].filter((i) => i.n > 0);

  if (p.loading && items.length === 0) {
    return (
      <p className="flex items-center gap-2 px-4 py-2 text-[12px] text-on-surface-variant">
        <Loader2 size={13} className="animate-spin" /> Conferindo o que não depende da agenda…
      </p>
    );
  }
  if (items.length === 0) {
    return (
      <p className="px-4 py-1 text-[12.5px] leading-relaxed text-on-surface-variant">
        Nada pendente fora da agenda — a caixa de pendências está limpa.
      </p>
    );
  }
  return (
    <>
      {items.map((i) => (
        <button
          key={i.key}
          type="button"
          onClick={onOpen}
          className="flex w-full items-center gap-2.5 px-4 py-2 text-left transition-colors hover:bg-surface-container/60"
        >
          <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-md ${i.tone}`}>
            <Inbox size={12} />
          </span>
          <span className="min-w-0 flex-1 text-[12.5px] leading-snug">
            {i.label}
            <small className="mt-px block text-[11px] text-on-surface-variant">{i.hint}</small>
          </span>
          <span className="num-mono shrink-0 text-[12.5px] font-semibold text-on-surface-variant">{i.n}</span>
        </button>
      ))}
    </>
  );
};

export const DayAgendaDrawer = () => {
  const { clinicId } = useClinic();
  const { user, role } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    setDockWidth, setHostMounted,
    open: dockOpen, view, openView, closeDock, toggleView,
    views, setSlotEl,
  } = useAgendaDock();

  // A vista da agenda vale em TODA rota, inclusive na própria AgendaPage.
  // Por um tempo ela ficou de fora de /agenda ("a agenda já está na tela"),
  // mas as duas não mostram a mesma coisa: a grade responde "como está o
  // dia inteiro" e o painel responde "e agora?" — próximo, sala de espera
  // com o cronômetro de cada um, fechamento financeiro. Quem
  // atende quer os dois ao mesmo tempo.
  const onAgendaPage = pathname === '/agenda';
  // Hospedeiro do painel: existe em toda rota com clínica.
  const hostActive = !!clinicId;

  // "Manter sempre aberta" — lido do navegador. Quando ligado, a agenda já
  // nasce aberta e não fecha ao clicar fora.
  const [pinned, setPinned] = useState(() => {
    try { return localStorage.getItem(ALWAYS_OPEN_STORAGE_KEY) === '1'; } catch { return false; }
  });
  // `open` = painel aberto NA VISTA DA AGENDA (as demais vistas moram no
  // mesmo painel, mas o conteúdo da agenda só carrega quando é ela que está
  // à mostra). O estado aberto/fechado vive no AgendaDockContext, compartilhado
  // com o Shell e com as outras vistas.
  const open = dockOpen && view === 'agenda';
  const setOpen = useCallback(
    (v: boolean) => { if (v) openView('agenda'); else closeDock(); },
    [openView, closeDock],
  );
  // Largura da janela — decide se a agenda ENCAIXA ao lado ou sobrepõe, e
  // quanto espaço o conteúdo precisa ceder. Atualiza ao redimensionar.
  const [vw, setVw] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  // "Manter sempre aberta" só age onde o painel fica AO LADO do conteúdo
  // (≥sm). No celular ele é tela cheia: fixado, cobriria a ficha aberta pelo
  // toque no nome. A preferência segue a conta (prefsNaConta), então o
  // celular herda a do computador — aqui ela só não vale.
  const pinnedHere = pinned && vw >= 640;
  // Tamanho quando aberto: false = parcial (compacto, 388px) · true =
  // ampliado (largo, agenda inteira em 2 colunas). Sempre abre em parcial;
  // a progressão é controlada pelo ciclo do Alt+A / puxador (ver abaixo).
  const [expanded, setExpanded] = useState(false);
  // Hoje em TODAS as unidades (como vem do listener); a tela lê `appts`,
  // só da unidade da Agenda (spec 019, fase 2 — mesma regra da AgendaPage).
  const [rawAppts, setAppts] = useState<Appointment[]>([]);
  const { filter: filterAgendaUnit, tagFor: unitTagFor } = useAgendaUnit();
  const appts = useMemo(() => filterAgendaUnit(rawAppts), [rawAppts, filterAgendaUnit]);
  const [loading, setLoading] = useState(false);
  // Busca por nome do paciente na lista do dia (acento-insensível). Só filtra
  // a exibição — contadores, financeiro e o "próximo" seguem no dia inteiro.
  const [search, setSearch] = useState('');
  // Toggle "DO DIA": ligado, a busca única filtra só os agendados de hoje
  // (lista abaixo); desligado, é a busca global de pacientes (abre a ficha).
  const [dayOnly, setDayOnly] = useState(false);
  // Termo levado do escopo "do dia" pro escopo "toda a base" quando o médico
  // usa a saída da busca sem resultado — a busca global já nasce com ele.
  const [globalSeed, setGlobalSeed] = useState('');
  // "Agora" (epoch ms) — mantém viva a minutagem de espera. Atualiza
  // enquanto a gaveta está montada (aberta ou no trilho).
  const [nowMs, setNowMs] = useState(() => Date.now());
  // Chamadas ativas (fila de HOJE em status CALLED) por appointmentId —
  // listener em tempo real só enquanto a gaveta está aberta. Mesma fonte
  // que o destaque "CHAMADO" da AgendaPage.
  const [calledById, setCalledById] = useState<Record<string, QueueEntry>>({});
  // Agendamento sendo chamado agora (spinner no botão) + último erro.
  const [callingId, setCallingId] = useState<string | null>(null);
  const [callError, setCallError] = useState<string | null>(null);
  // Agendamento sendo marcado/desmarcado como próximo (spinner na estrela).
  const [markingId, setMarkingId] = useState<string | null>(null);
  // Alerta de acompanhante disparado logo após marcar como próximo — guarda
  // o agendamento marcado (null = fechado).
  const [accompanistAsk, setAccompanistAsk] = useState<Appointment | null>(null);
  // SECRETÁRIA → médico: "Avisar médico" do cartão PRÓXIMO (pop-up na tela
  // do médico dizendo qual paciente está entrando).
  const {
    notify: notifyDoctor,
    sendingId: doctorAlertSendingId,
    sentId: doctorAlertSentId,
    modal: notifyDoctorModal,
  } = useNotifyDoctor(clinicId, { onError: setCallError });
  // Duração de 1 slot (config da clínica) — data cada procedimento da
  // visita (início + i × slot). Carregada ao abrir a gaveta.
  const [slotMinutes, setSlotMinutes] = useState(20);
  // Horário que separa manhã/tarde (config `shiftBoundary` da clínica) — onde
  // a lista desenha a divisória "Tarde". Default 13:00; editável em
  // Configurações › Agenda. Carregado ao abrir a gaveta.
  const [shiftBoundary, setShiftBoundary] = useState('13:00');
  // Janela de atendimento de HOJE (`config.days[weekday]`) — é o que o estado
  // vazio usa pra dizer quantos horários estão abertos e de que hora a que
  // hora. null enquanto a config não chegou.
  const [todayWindow, setTodayWindow] = useState<AgendaConfigDay | null>(null);
  // Agendamentos dos próximos dias — carregados SÓ quando hoje está vazio,
  // pra o estado vazio mostrar "o que vem" e quem poderia vir hoje.
  const [rawUpcoming, setUpcoming] = useState<Appointment[]>([]);
  const upcoming = useMemo(() => filterAgendaUnit(rawUpcoming), [rawUpcoming, filterAgendaUnit]);
  // Bloco "encerrados" (concluídos e faltas) recolhido por padrão.
  const [showClosed, setShowClosed] = useState(false);
  // Map<insurerId, nome do convênio> — resolve o selo de convênio de cada
  // card (sem id = Particular). Mesma fonte que a agenda completa.
  const [insurerNameById, setInsurerNameById] = useState<Record<string, string>>({});
  // Map<patientId, idade em anos> — o agendamento não guarda data de
  // nascimento; buscamos do cadastro em lote (mesma fonte/padrão da
  // AgendaPage) só pros pacientes ainda não carregados.
  const [patientAgeById, setPatientAgeById] = useState<Record<string, number>>({});
  const knownPatientIdsRef = useRef<Set<string>>(new Set());
  // Cartão do PRÓXIMO paciente + o container rolável da lista — usados pra
  // centralizar o próximo na vista quando a gaveta abre.
  const nextRowRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  // Refs do painel e da alça — pra detectar clique FORA e recolher.
  const asideRef = useRef<HTMLElement | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);

  // A gaveta DA AGENDA (dados do dia, trilho com contadores, alça) existe em
  // toda tela com clínica — a AgendaPage inclusive.
  const drawerActive = hostActive;

  // ── Sanfona (por máquina, desligada por padrão) ──
  // Agenda FECHADA + mouse parado no trilho → a agenda ESPIA: abre por cima
  // do sistema, sem encaixar (o conteúdo não encolhe nem re-quebra), e
  // recolhe quando o mouse sai do trilho e do painel. Foco dentro do painel
  // (busca, um botão clicado) segura aberta; clicar fora recolhe como sempre.
  // Clicar no trilho durante a espiada "assume" a agenda: vira a abertura
  // normal, encaixada. Com "manter sempre aberta" nada disso entra em jogo.
  // Espiando, o painel ganha sombra mais funda (flutua sobre o sistema).
  const sanfona = useCockpitSanfonaEnabled();
  const [peek, setPeek] = useState(false);
  const peekRef = useRef(false);
  peekRef.current = peek;
  const peekHover = useRef(false);
  const peekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPeekTimer = () => {
    if (peekTimer.current) clearTimeout(peekTimer.current);
    peekTimer.current = null;
  };
  /** Mouse entrou na zona da agenda no trilho (ou no painel espiado). */
  const peekEnter = (e: ReactPointerEvent) => {
    peekHover.current = true;
    clearPeekTimer();
    // Toque não tem "parar em cima": no celular/tablet o toque abre normal.
    if (!sanfona || dockOpen || e.pointerType === 'touch') return;
    peekTimer.current = setTimeout(() => {
      setPeek(true);
      setExpanded(false);
      openView('agenda');
    }, PEEK_OPEN_MS);
  };
  const peekLeave = () => {
    peekHover.current = false;
    clearPeekTimer();
    if (!peekRef.current) return;
    peekTimer.current = setTimeout(() => {
      if (peekHover.current || asideRef.current?.contains(document.activeElement)) return;
      closeDock();
    }, PEEK_CLOSE_MS);
  };
  // Painel fechou (ou trocou de vista) → acabou a espiada.
  useEffect(() => {
    if (!open) setPeek(false);
  }, [open]);
  useEffect(() => () => clearPeekTimer(), []);
  // Com a sanfona, os dados de apoio (convênios, idades, fila, config) ficam
  // carregados depois da primeira abertura — senão cada espiada refaria as
  // leituras e os selos piscariam. Sem a sanfona, segue carregando a cada
  // abertura como sempre.
  const [warm, setWarm] = useState(false);
  useEffect(() => {
    if (sanfona && open) setWarm(true);
  }, [sanfona, open]);
  const loadOpen = open || warm;

  // Agendamentos de HOJE em TEMPO REAL. Antes só ligava com a gaveta aberta;
  // agora acompanha a gaveta MONTADA, porque o trilho recolhido também mostra
  // dado vivo (cronômetro, próximo, espinha do dia, atendidos/total) — um
  // trilho com número congelado seria pior que trilho nenhum. Continua sendo
  // UM listener, e desliga na AgendaPage (que já tem o seu).
  useEffect(() => {
    if (!drawerActive || !clinicId) return;
    setNowMs(Date.now());
    setAppts((prev) => {
      if (prev.length === 0) setLoading(true);
      return prev;
    });
    const now = new Date();
    const apptQuery = query(
      collection(db, 'clinics', clinicId, 'appointments'),
      where('date', '>=', startOfDay(now)),
      where('date', '<=', endOfDay(now)),
    );
    const unsub = onSnapshot(
      apptQuery,
      (snap) => {
        const res = snap.docs
          .map((d) => normalizeAppointment(d.data()))
          .filter((a) => !a.isDeleted);
        res.sort(
          (a, b) =>
            a.startTime.localeCompare(b.startTime) ||
            a.endTime.localeCompare(b.endTime),
        );
        setAppts(res);
        setLoading(false);
      },
      (err) => {
        console.error('[day-agenda] appointments snapshot falhou:', err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [drawerActive, clinicId]);

  // Idade do paciente por id — busca em lote só pra quem ainda não foi
  // carregado nesta abertura da gaveta. Reseta ao fechar/trocar de clínica
  // (mesmo padrão de refetch da AgendaPage).
  useEffect(() => {
    if (!loadOpen) {
      knownPatientIdsRef.current = new Set();
      setPatientAgeById({});
    }
  }, [loadOpen]);
  useEffect(() => {
    if (!loadOpen || !clinicId || appts.length === 0) return;
    const ids = Array.from(new Set(appts.map((a) => a.patientId).filter(Boolean)))
      .filter((id) => !knownPatientIdsRef.current.has(id));
    if (ids.length === 0) return;
    let cancelled = false;
    ids.forEach((id) => knownPatientIdsRef.current.add(id));
    PatientService.getPatientsByIds(clinicId, ids)
      .then((patients) => {
        if (cancelled) return;
        const add: Record<string, number> = {};
        for (const p of patients) {
          const age = calculateAgeFromBirthDate(p.birthDate);
          if (age != null) add[p.id] = age;
        }
        if (Object.keys(add).length > 0) {
          setPatientAgeById((prev) => ({ ...prev, ...add }));
        }
      })
      .catch((err) => {
        console.warn('[day-agenda] falha ao carregar idade dos pacientes:', err);
        ids.forEach((id) => knownPatientIdsRef.current.delete(id));
      });
    return () => { cancelled = true; };
  }, [loadOpen, clinicId, appts]);

  // Tique de relógio — mantém viva a minutagem de espera e a contagem
  // regressiva do próximo. Roda também no trilho recolhido (o cronômetro em
  // minutos vive dele); os segundos do cartão "Agora" vêm do <Chrono/>, que
  // tem intervalo próprio.
  useEffect(() => {
    if (!drawerActive) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [drawerActive]);

  // Acompanha a largura da janela pra recalcular o encaixe (ver `docked`).
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Config da clínica — duração do slot (data cada procedimento), a divisa
  // manhã/tarde (`shiftBoundary`, pra divisória "Tarde") e a janela de
  // atendimento de hoje (usada pelo estado vazio). Carrega ao abrir; falha
  // silenciosa mantém os defaults (20 min · 13:00).
  const [todayRanges, setTodayRanges] = useState<{ reserved: ReleasedRange[]; blocked: BlockedRange[] }>({ reserved: [], blocked: [] });
  useEffect(() => {
    if (!loadOpen || !clinicId) return;
    let cancelled = false;
    AgendaConfigService.getConfig(clinicId)
      .then((cfg) => {
        if (cancelled) return;
        if (cfg.defaultDuration > 0) setSlotMinutes(cfg.defaultDuration);
        if (cfg.shiftBoundary) setShiftBoundary(cfg.shiftBoundary);
        const weekday = new Date().getDay() as 0 | 1 | 2 | 3 | 4 | 5 | 6;
        setTodayWindow(cfg.days[weekday] ?? null);
        // Reserva da secretária e bloqueio manual de HOJE (spec 007) — o
        // rail é sempre de hoje, então basta uma data. `getConfig` espalha
        // o doc cru, então `releases`/`blockedDates` vêm junto.
        const raw = cfg as unknown as { releases?: unknown; blockedDates?: unknown };
        const boundary = cfg.shiftBoundary || undefined;
        const today = new Date();
        const key = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        setTodayRanges({
          reserved: reservedRanges(parseReleases(raw.releases, boundary).get(key)),
          blocked: parseBlockedDates(raw.blockedDates, boundary).get(key) ?? [],
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [loadOpen, clinicId]);

  // Cadastro de convênios pra resolver `insurerId → nome` no selo de cada
  // card. Carrega ao abrir; mudanças no cadastro são raras, então um fetch
  // por abertura basta. Falha silenciosa só deixa o selo cair em "Convênio".
  useEffect(() => {
    if (!loadOpen || !clinicId) return;
    let cancelled = false;
    getDocs(collection(db, 'clinics', clinicId, 'insurers'))
      .then((snap) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        snap.docs.forEach((d) => {
          const data = d.data() as { name?: string };
          if (data.name) map[d.id] = data.name;
        });
        setInsurerNameById(map);
      })
      .catch((err) => console.error('[day-agenda] convênios load falhou:', err));
    return () => { cancelled = true; };
  }, [loadOpen, clinicId]);

  // Listener em tempo real da fila de HOJE (só enquanto aberta) — destaca
  // quem está CHAMADO e confirma na hora a chamada feita daqui. Filtra
  // status client-side pra usar range simples em arrivedAt (sem índice
  // composto), igual à AgendaPage.
  useEffect(() => {
    if (!loadOpen || !clinicId) return;
    const queueQuery = query(
      collection(db, 'clinics', clinicId, 'queue'),
      where('arrivedAt', '>=', startOfDay(new Date())),
    );
    const unsub = onSnapshot(
      queueQuery,
      (snap) => {
        const map: Record<string, QueueEntry> = {};
        snap.docs.forEach((d) => {
          const e = d.data() as QueueEntry;
          if (e.appointmentId && e.status === 'CALLED') map[e.appointmentId] = e;
        });
        setCalledById(map);
      },
      (err) => console.error('[day-agenda] queue snapshot falhou:', err),
    );
    return () => unsub();
  }, [loadOpen, clinicId]);

  // Próximos dias — SÓ quando hoje está sem agendamento. É o que transforma
  // o estado vazio em ponto de partida ("o que vem", "poderiam vir hoje") em
  // vez de um beco com a frase "nenhum agendamento para hoje".
  useEffect(() => {
    if (!loadOpen || !clinicId || loading || appts.length > 0) return;
    let cancelled = false;
    const t = new Date();
    const from = new Date(t.getFullYear(), t.getMonth(), t.getDate() + 1).getTime();
    const to = new Date(t.getFullYear(), t.getMonth(), t.getDate() + 1 + UPCOMING_DAYS).getTime() - 1;
    getDocs(
      query(
        collection(db, 'clinics', clinicId, 'appointments'),
        where('date', '>=', from),
        where('date', '<=', to),
      ),
    )
      .then((snap) => {
        if (cancelled) return;
        const list = snap.docs
          .map((d) => normalizeAppointment(d.data()))
          .filter((a) => !a.isDeleted && a.status !== 'CANCELADA');
        list.sort((a, b) => a.date - b.date || a.startTime.localeCompare(b.startTime));
        setUpcoming(list);
      })
      .catch((err) => console.warn('[day-agenda] próximos dias falhou:', err));
    return () => { cancelled = true; };
  }, [loadOpen, clinicId, loading, appts.length]);

  /**
   * Chama o paciente com UM clique: reaproveita a entrada de fila ativa
   * (ou faz o check-in na hora, bumpando o status pra EM_ESPERA) e dispara
   * a chamada pro painel/recepção na sala salva do médico (mesma chave da
   * QueuePage / painel rápido da agenda). O selo "CHAMADO" e o novo status
   * aparecem sozinhos via os listeners em tempo real acima.
   */
  const handleCall = useCallback(async (a: Appointment) => {
    if (!clinicId || !user || callingId) return;
    setCallingId(a.id);
    setCallError(null);
    try {
      const existing =
        calledById[a.id] ?? (await QueueService.findActiveByAppointment(clinicId, a.id));
      const entry =
        existing ??
        (await QueueService.checkInFromAppointment(clinicId, a, null, {
          uid: user.uid,
          name: user.displayName || user.email || undefined,
        }));
      const room =
        localStorage.getItem(ROOM_STORAGE_KEY(user.uid))?.trim() || 'Consultório 1';
      await QueueService.callPatient(clinicId, entry.id, room, user.uid);
    } catch (err) {
      console.error('[day-agenda] chamada falhou:', err);
      setCallError(
        `Falha ao chamar ${a.patientName || 'paciente'}. Tente de novo ou use a agenda.`,
      );
    } finally {
      setCallingId(null);
    }
  }, [clinicId, user, callingId, calledById]);


  // Abre/fecha pela alça da borda: fechado → abre em parcial; aberto
  // (parcial OU ampliado) → recolhe totalmente. Igual em qualquer tamanho,
  // então no modo ampliado também fecha de uma vez.
  const toggleDrawer = useCallback(() => {
    // Espiando (sanfona): o clique "assume" a agenda — fica aberta e encaixa.
    if (peekRef.current) { setPeek(false); return; }
    if (open) setOpen(false);
    else { setExpanded(false); setOpen(true); }
  }, [open, setOpen]);

  // Clique num ícone do trilho: a agenda usa o ciclo acima; as demais vistas
  // abrem/fecham direto (clicar na ativa recolhe o painel).
  const onRailClick = useCallback((v: DockView) => {
    if (v === 'agenda') toggleDrawer();
    else toggleView(v);
  }, [toggleDrawer, toggleView]);

  // Ciclo do Alt+A: fechado → parcial → ampliado → fechado. Cada toque
  // avança um passo (abre parcial, depois amplia, depois recolhe tudo).
  const cycleDrawer = useCallback(() => {
    if (peekRef.current) { setPeek(false); return; }    // espiando → assume (parcial)
    if (!open) { setExpanded(false); setOpen(true); }   // fechado → parcial
    else if (!expanded) setExpanded(true);              // parcial → ampliado
    else setOpen(false);                                // ampliado → fechado
  }, [open, expanded, setOpen]);

  // Liga/desliga o "manter sempre aberta" e persiste por navegador. Ao ligar,
  // já traz a agenda à frente.
  const togglePinned = useCallback(() => {
    setPeek(false);
    setPinned((prev) => {
      const next = !prev;
      try { localStorage.setItem(ALWAYS_OPEN_STORAGE_KEY, next ? '1' : '0'); } catch { /* storage indisponível */ }
      if (next) setOpen(true);
      return next;
    });
  }, [setOpen]);

  // Escopo da busca. Abrir pra base inteira LEVA o termo digitado junto (a
  // busca global nasce com ele); voltar pro dia limpa a semente. Mantém o
  // comportamento antigo do chip: sair do "do dia" limpa o filtro da lista.
  const widenScope = useCallback((term: string) => {
    setGlobalSeed(term);
    setSearch('');
    setDayOnly(false);
  }, []);
  const narrowScope = useCallback(() => {
    setGlobalSeed('');
    setDayOnly(true);
  }, []);

  // Atalho de teclado: Alt+A percorre o ciclo acima; ESC fecha de qualquer
  // estado. Exige Alt SEM Ctrl, então o AltGr (= Ctrl+Alt nos teclados ABNT)
  // não dispara nem atrapalha a digitação de caracteres especiais. Por ser
  // combo deliberado, funciona mesmo com o foco num campo de texto.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Modal aberto por cima (trava o scroll do body): Esc é dele.
        if (document.body.style.overflow === 'hidden') return;
        closeDock();
        return;
      }
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.code === TOGGLE_CODE) {
        e.preventDefault();
        cycleDrawer();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cycleDrawer, closeDock]);

  // Clicar fora recolhe a gaveta. Como o painel é não-modal (sem backdrop,
  // o fundo continua visível e clicável), escutamos cliques no documento:
  // qualquer mousedown fora do <aside> E fora da alça fecha. O clique que
  // ABRE não dispara isto — o listener só é montado depois de `open` virar
  // true, então o mousedown da abertura já passou. Continua fechando também
  // por X, Esc, Alt+A e pela própria alça.
  // Com "manter sempre aberta" (pinned) ligado, NÃO fecha ao clicar fora — a
  // agenda fica encaixada ao lado enquanto o médico trabalha no sistema.
  useEffect(() => {
    if (!open || pinnedHere) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // Modais (ex.: pergunta da acompanhante) renderizam via portal no
      // <body> — cliques neles não podem recolher a gaveta, senão o modal
      // desmonta junto.
      if (isInsideModalPortal(target)) return;
      if (asideRef.current?.contains(target)) return;
      if (railRef.current?.contains(target)) return;
      // Os controles de tela da barra do topo (recolher/expandir laterais e
      // tela cheia) MANDAM no painel — o mousedown deles não pode fechá-lo
      // antes do clique agir, senão o botão inverte de fase e passa a mexer
      // num lado por clique. Só quem não usa "manter sempre aberta" via isso.
      if (target instanceof Element && target.closest('[data-dock-control]')) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [open, pinnedHere, setOpen]);

  // Mesma ideia, mas para a vista do CHAT dentro do painel: clicar fora
  // BAIXA o chat e devolve a agenda do dia — o painel continua aberto. Quem
  // volta a trabalhar no prontuário/agenda quer a lista do dia de novo, não
  // uma conversa tapando o painel nem um painel fechado. As demais vistas
  // (resumo, optotipos, ficha) ficam de fora de propósito: elas existem
  // justamente para serem consultadas ENQUANTO se clica no resto da tela.
  useEffect(() => {
    if (!dockOpen || view !== 'chat') return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (isInsideModalPortal(target)) return;
      if (asideRef.current?.contains(target)) return;
      if (railRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[data-dock-control]')) return;
      openView('agenda');
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [dockOpen, view, openView]);

  // ENCAIXE (não cobrir): quando a agenda abre numa tela com espaço, ela
  // reserva a própria largura à direita e o conteúdo do sistema se encolhe
  // pra caber ao lado — em vez de ficar coberto. Em telas estreitas (ou no
  // modo ampliado sem espaço), `vw - painel` fica abaixo do mínimo e a gaveta
  // volta a abrir POR CIMA (reserva 0). Na própria AgendaPage e sem clínica,
  // a gaveta não aparece, então também não reserva nada.
  const viewPx = view === 'agenda' ? (expanded ? WIDE_WIDTH : NORMAL_WIDTH) : VIEW_WIDTH[view];
  const panelPx = Math.min(viewPx, vw * 0.94);
  const panelShown = dockOpen;
  const voiceDockPx = useVoiceDockPx();
  // Trilho: no desktop (≥sm), em qualquer rota com clínica — a vista da
  // agenda sozinha já justifica o trilho.
  const railShown = vw >= 640 && hostActive;
  const railPx = railShown ? RAIL_WIDTH : 0;
  // Espiando (sanfona), nunca encaixa: o conteúdo não pode encolher só
  // porque o mouse passou no trilho.
  // A coluna do ditado aberta também tira largura do conteúdo.
  const docked =
    panelShown && !peek && hostActive && vw - voiceDockPx - railPx - panelPx >= DOCK_MIN_CONTENT;
  const dockWidth = hostActive
    ? `${Math.round(railPx + (docked ? panelPx : 0))}px`
    : '0px';
  useEffect(() => {
    setDockWidth(dockWidth);
    // Ao desmontar, devolve o espaço ao conteúdo.
    return () => setDockWidth('0px');
  }, [dockWidth, setDockWidth]);

  // Avisa as vistas (chat, resumo, optotipos) que há um hospedeiro nesta
  // rota — elas então moram no painel em vez de abrir gaveta própria.
  useEffect(() => {
    setHostMounted(hostActive);
    return () => setHostMounted(false);
  }, [hostActive, setHostMounted]);

  const goToPatient = (patientId: string) => {
    // Encaixada (pinned) não fecha ao navegar — continua ao lado da ficha.
    // Espiando (sanfona) também não: fica enquanto o mouse estiver nela e
    // recolhe quando ele sai (o clique não pode segurá-la aberta depois).
    if (peekRef.current) releaseClickFocus(asideRef.current);
    else if (!pinnedHere) setOpen(false);
    navigate(`/pacientes/${patientId}`);
  };

  const goToAgenda = (isoDate?: string) => {
    setOpen(false);
    navigate(isoDate ? `/agenda?date=${isoDate}` : '/agenda');
  };

  // Alterna direto entre parcial e ampliado enquanto o painel está aberto
  // (botão de tamanho no cabeçalho) — não fecha.
  const toggleExpanded = useCallback(() => setExpanded((v) => !v), []);

  /**
   * Marca / desmarca manualmente o paciente como "próximo a ser atendido".
   * É a ÚNICA forma de acender o destaque âmbar — não há mais derivação
   * automática. markAsNextUp limpa qualquer outro marcado do dia (só um
   * próximo por vez); o listener em tempo real reflete na hora.
   */
  const handleToggleNext = useCallback(async (a: Appointment) => {
    if (!clinicId || !user || markingId) return;
    setMarkingId(a.id);
    setCallError(null);
    try {
      if (a.isNextUp) {
        await AppointmentService.unmarkAsNextUp(clinicId, a.id);
      } else {
        await AppointmentService.markAsNextUp(clinicId, a.id, user.uid);
        // Marcou como próximo → pergunta se o paciente entra acompanhado
        // (o modal grava o acompanhante do dia no agendamento).
        setAccompanistAsk(a);
      }
    } catch (err) {
      console.error('[day-agenda] marcar próximo falhou:', err);
      setCallError(`Não foi possível marcar ${a.patientName || 'o paciente'} como próximo.`);
    } finally {
      setMarkingId(null);
    }
  }, [clinicId, user, markingId]);

  // Id do PRÓXIMO paciente — regra completa (`pickNextUp`): marcação manual
  // (sticky) vence; senão dilatação pronta; senão menor horário entre os que
  // já estão aguardando (encaixe pela chegada), com desempate por chegada. A
  // estrela manual continua sendo a forma de fixar alguém; sem marca, o
  // destaque âmbar é derivado e se move sozinho ao concluir/iniciar atendimento.
  const nextId = useMemo(
    () => pickNextUp(appts, { calledByAppointmentId: calledById }),
    [appts, calledById],
  );

  // Centraliza o cartão do PRÓXIMO na vista quando a gaveta abre (e quando o
  // próximo muda com ela aberta — ex.: após chamá-lo, o destaque passa pro
  // seguinte). Robusto de propósito: o jeito antigo (um setTimeout fixo de
  // 150ms + scrollIntoView) caía antes dos dados do Firestore chegarem na
  // PRIMEIRA abertura e no meio da animação de entrada (250ms), então quase
  // nunca rolava. Aqui tentamos a cada frame até o cartão existir no DOM e
  // rolamos SÓ o container da lista — o destino é calculado na vertical por
  // getBoundingClientRect; como a animação de entrada é translateX (puramente
  // horizontal), a conta vertical fica correta mesmo durante o slide, e não
  // arrastamos a página de trás (a gaveta é não-modal).
  useEffect(() => {
    if (!open || !nextId) return;
    let raf = 0;
    let tries = 0;
    const tick = () => {
      const list = listRef.current;
      const el = nextRowRef.current;
      if (list && el) {
        const lr = list.getBoundingClientRect();
        const er = el.getBoundingClientRect();
        const delta = er.top - lr.top - (list.clientHeight - el.clientHeight) / 2;
        list.scrollTo({ top: Math.max(0, list.scrollTop + delta), behavior: 'smooth' });
        return;
      }
      // Cartão/lista ainda não pintaram (dados chegando) — tenta nos próximos
      // frames (~1s no máximo) antes de desistir silenciosamente.
      if (tries++ < 60) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, nextId]);

  const counts = useMemo(() => {
    const active = appts.filter((a) => a.status !== 'CANCELADA');
    const done = appts.filter((a) => a.status === 'CONCLUIDA').length;
    const lastEnd = active.reduce((acc, a) => (a.endTime > acc ? a.endTime : acc), '');
    return { total: active.length, done, lastEnd };
  }, [appts]);

  // Lista exibida: aplica a busca por nome (acento-insensível) só na pintura.
  // Sem termo, mostra o dia inteiro como antes.
  const trimmedSearch = search.trim();
  const searching = dayOnly && trimmedSearch.length > 0;
  const filteredAppts = useMemo(() => {
    const q = normalizeForSearch(trimmedSearch);
    if (!q) return appts;
    return appts.filter((a) => normalizeForSearch(a.patientName ?? '').includes(q));
  }, [appts, trimmedSearch]);

  // Quem está em atendimento AGORA (normalmente um) e quem é o próximo —
  // ambos saem da lista corrida e viram cartão próprio no topo.
  const liveAppts = useMemo(
    () => appts.filter((a) => a.status === 'EM_ATENDIMENTO'),
    [appts],
  );
  const nextAppt = useMemo(
    () => (nextId ? appts.find((a) => a.id === nextId) ?? null : null),
    [appts, nextId],
  );

  /**
   * Distribui o dia nos blocos que a gaveta desenha. Todo agendamento cai em
   * exatamente um bloco — nada some. Fora da busca, quem já virou cartão
   * (agora / próximo) não se repete na lista.
   */
  const sections = useMemo(() => {
    const skip = new Set<string>();
    if (!searching) {
      liveAppts.forEach((a) => skip.add(a.id));
      if (nextAppt) skip.add(nextAppt.id);
    }
    const waiting: Appointment[] = [];
    const rest: Appointment[] = [];
    const closed: Appointment[] = [];
    for (const a of filteredAppts) {
      if (skip.has(a.id)) continue;
      if (a.status === 'CONCLUIDA' || a.status === 'FALTOU' || a.status === 'CANCELADA') closed.push(a);
      else if (a.status === 'EM_ESPERA' || a.status === 'EM_ATENDIMENTO' || a.checkedInAt) waiting.push(a);
      else rest.push(a);
    }
    // Sala de espera ordenada por quem chegou primeiro — é a fila real.
    waiting.sort((a, b) => (a.checkedInAt ?? 0) - (b.checkedInAt ?? 0));
    return { waiting, rest, closed };
  }, [filteredAppts, searching, liveAppts, nextAppt]);

  /** Maior espera da sala — alimenta a frase que interpreta o número. */
  const worstWait = useMemo(() => {
    let worst: { a: Appointment; min: number } | null = null;
    for (const a of sections.waiting) {
      if (!a.checkedInAt || a.status !== 'EM_ESPERA') continue;
      const min = waitMinutes(nowMs - a.checkedInAt);
      if (!worst || min > worst.min) worst = { a, min };
    }
    return worst;
  }, [sections.waiting, nowMs]);

  /**
   * "Espinha do dia": um segmento por agendamento vivo, na ordem do relógio.
   * É a forma do dia em 7px de altura — quanto já passou, o que corre agora,
   * quem é o próximo e quanto falta.
   */
  const spine = useMemo(
    () =>
      appts
        .filter((a) => a.status !== 'CANCELADA')
        .map((a) => {
          if (a.status === 'EM_ATENDIMENTO') return 'now' as const;
          if (a.status === 'CONCLUIDA') return 'done' as const;
          if (a.status === 'FALTOU') return 'miss' as const;
          if (a.id === nextId) return 'next' as const;
          return 'todo' as const;
        }),
    [appts, nextId],
  );

  // Resumo financeiro do dia — soma só as cobranças efetivamente lançadas
  // (chargedAt), separando o que já entrou (recebido) do que falta receber.
  // Mesma regra dos totais da agenda completa / BalanceModal, então os
  // números fecham entre as telas; e fecham entre si: recebido + a receber
  // = total. ISENTO conta como cobrança (cortesia) mas soma R$ 0.
  const financ = useMemo(() => {
    let recebido = 0;
    let aReceber = 0;
    let count = 0;
    for (const a of appts) {
      if (!hasChargeRecord(a)) continue;
      count += 1;
      recebido += amountReceived(a);
      aReceber += remainingBalance(a);
    }
    return { recebido, aReceber, total: recebido + aReceber, count };
  }, [appts]);

  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }),
    // Recalcula quando abre (cobre virada de dia com a app aberta).
    [open], // eslint-disable-line react-hooks/exhaustive-deps
  );

  /** Próximos dias com agenda, agrupados — matéria-prima do estado vazio. */
  const upcomingDays = useMemo(() => {
    const byDay = new Map<string, Appointment[]>();
    for (const a of upcoming) {
      const k = dateKey(a.date);
      const arr = byDay.get(k);
      if (arr) arr.push(a);
      else byDay.set(k, [a]);
    }
    return Array.from(byDay.entries())
      .slice(0, 3)
      .map(([key, items]) => {
        const d = new Date(`${key}T12:00:00`);
        return {
          key,
          weekday: d.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', ''),
          label: d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
          count: items.length,
          first: items[0]?.startTime ?? '',
          last: items.reduce((acc, x) => (x.endTime > acc ? x.endTime : acc), ''),
          unconfirmed: items.filter((x) => x.status === 'PENDENTE' || x.status === 'AGENDADA').length,
        };
      });
  }, [upcoming]);

  /** Quem está marcado em breve e ainda não confirmou — cabe no dia livre. */
  const couldComeToday = useMemo(() => {
    const seen = new Set<string>();
    const out: { a: Appointment; when: string }[] = [];
    for (const a of upcoming) {
      if (a.status !== 'PENDENTE' && a.status !== 'AGENDADA') continue;
      if (!a.patientId || seen.has(a.patientId)) continue;
      seen.add(a.patientId);
      const d = new Date(a.date);
      out.push({
        a,
        when: `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} · ${a.startTime}`,
      });
      if (out.length === 4) break;
    }
    return out;
  }, [upcoming]);

  /** Horários abertos hoje = janela do expediente ÷ duração do slot. */
  const openSlots = useMemo(() => {
    if (!todayWindow?.enabled) return 0;
    const span = hmToMinutes(todayWindow.end) - hmToMinutes(todayWindow.start);
    if (span <= 0 || slotMinutes <= 0) return 0;
    return Math.max(0, Math.floor(span / slotMinutes) - counts.total);
  }, [todayWindow, slotMinutes, counts.total]);

  if (!clinicId) return null;

  // Largura do painel conforme o nível, capada em 94vw pra não estourar
  // telas menores. Vira tanto a largura do <aside> (via --agenda-w) quanto
  // o deslocamento da alça, que acompanha a borda esquerda do painel.
  const panelWidthCss = `min(${viewPx}px, 94vw)`;
  const pad = expanded ? 'px-5' : 'px-4';
  const gridCols = expanded ? 'grid grid-cols-1 sm:grid-cols-2' : '';

  // ── Minutos até o próximo / do atendimento em curso (trilho e cartões) ──
  const liveStartedAt = liveAppts.find((a) => a.attendanceStartedAt)?.attendanceStartedAt;
  const liveMinutes = liveStartedAt ? waitMinutes(nowMs - liveStartedAt) : null;
  const nextInMinutes = (() => {
    if (!nextAppt || nextAppt.noTime) return null;
    const d = new Date(nowMs);
    const nowMin = d.getHours() * 60 + d.getMinutes();
    return hmToMinutes(nextAppt.startTime) - nowMin;
  })();

  /** Botão-ícone quieto usado nas ações de linha e no cabeçalho. */
  const iconBtnClass =
    'grid h-7 w-7 shrink-0 place-items-center rounded-md text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface disabled:opacity-50';

  /**
   * Uma linha do dia. Mantém TODOS os selos que a gaveta já mostrava
   * (urgência, chamado, próximo, status, dilatação, convênio, cobrança,
   * espera) — o que mudou é o peso visual: barra de cor à esquerda, horário
   * em mono, nome legível e selos em pílulas quietas. `showStatus` desliga o
   * selo de status onde a própria seção já diz o status (sala de espera).
   */
  const renderRow = (
    a: Appointment,
    opts: { showStatus?: boolean; showWaitClock?: boolean } = {},
  ) => {
    const { showStatus = true, showWaitClock = false } = opts;
    const done = a.status === 'CONCLUIDA';
    const cancelled = a.status === 'CANCELADA';
    const faltou = a.status === 'FALTOU';
    const isNext = a.id === nextId;
    const charged = hasChargeRecord(a);
    const money = a.chargedAmount ?? a.expectedPrice;
    // "Chamado" só vale até atender: depois de atendido/cancelado/faltou o
    // selo CHAMADO some, mesmo que a fila ainda esteja em CALLED.
    const called = done || cancelled || faltou ? undefined : calledById[a.id];
    // Chamar fica SEMPRE disponível (qualquer status, inclusive concluído/
    // cancelado): o médico pode reanunciar o paciente a qualquer momento.
    const callable = !!user;
    // Pode marcar como próximo? Qualquer agendamento "vivo" (fora de
    // cancelado/concluído/faltou/em atendimento).
    const markable =
      !!user &&
      a.status !== 'CANCELADA' &&
      a.status !== 'CONCLUIDA' &&
      a.status !== 'FALTOU' &&
      a.status !== 'EM_ATENDIMENTO';
    // ── Presença física ──
    // Quem já está na clínica esperando ganha uma barra mais GROSSA na
    // linha — a mesma silhueta da faixa de presença dos cartões da agenda
    // (`[data-evt-present]` no index.css), pra as duas telas se lerem
    // igual — e um fundo VERDE leve na linha inteira (success-container, que
    // vira menta no tema claro e verde-escuro no escuro): quem já chegou e
    // quem ainda não se separam de relance, sem precisar ler selo nenhum. O
    // sage cru não servia — sobre o creme do tema claro ele vira cinza-oliva
    // e não lê como cor diferente.
    // A chegada só vale no dia do próprio agendamento (checkedInAt
    // herdado de remarcação não põe ninguém na sala de espera).
    const arrivedToday =
      a.checkedInAt !== undefined &&
      a.checkedInAt >= a.date &&
      a.checkedInAt - a.date < 86_400_000
        ? a.checkedInAt
        : undefined;
    const here =
      !done && !cancelled && !faltou && a.status !== 'EM_ATENDIMENTO' &&
      (a.status === 'EM_ESPERA' || arrivedToday !== undefined);
    const waitMin =
      showWaitClock && a.checkedInAt && a.status === 'EM_ESPERA'
        ? waitMinutes(nowMs - a.checkedInAt)
        : null;
    const waitDot =
      waitMin == null ? '' : waitMin >= WAIT_TARGET_MIN ? 'bg-terracotta' : waitMin >= WAIT_WARM_MIN ? 'bg-amber' : 'bg-sage';
    const waitText =
      waitMin == null ? '' : waitMin >= WAIT_TARGET_MIN ? 'text-terracotta font-semibold' : waitMin >= WAIT_WARM_MIN ? 'text-amber' : 'text-sage';
    const badge = 'inline-flex items-center gap-1 rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-wide';

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => goToPatient(a.patientId)}
        onKeyDown={(e) => { if (e.key === 'Enter') goToPatient(a.patientId); }}
        title="Abrir ficha do paciente"
        className={`group flex cursor-pointer items-start gap-2.5 py-2 transition-colors ${pad}
          ${here
            ? 'bg-success-container/30 hover:bg-success-container/45'
            : 'hover:bg-surface-container/60'}
          ${done || cancelled ? 'opacity-60' : ''}`}
      >
        {/* Barra de natureza/estado — a espinha da linha. */}
        <span
          className={`mt-1 shrink-0 rounded-sm ${here ? 'h-[22px] w-[5px]' : 'h-[18px] w-[3px]'} ${waitMin != null ? waitDot : rowBarTone(a)}`}
          aria-hidden
        />
        {/* Horário — "sem hora" mostra "A seguir" (horários sintéticos). */}
        <span className="mt-px w-[38px] shrink-0 text-right">
          {a.noTime ? (
            <span className="label-mono block leading-tight">A seguir</span>
          ) : (
            <span className="num-mono block text-[12px] leading-tight text-on-surface-variant">
              {a.startTime}
            </span>
          )}
        </span>

        <div className="min-w-0 flex-1">
          {/* O NOME TEM PRIORIDADE (como nas visões da agenda): a linha de
              cima é só dele (+ idade), na largura inteira da linha — as ações
              ficam embaixo, nos selos —, em negrito, e quebra em até 2 linhas
              em vez de cortar em "MARIA ALZE…". A idade fica `shrink-0` num
              item à parte pra nunca sumir; a minutagem de espera desceu pra
              linha do tipo, onde sobra espaço. */}
          <p className="flex min-w-0 items-baseline gap-1 text-[14px] leading-snug">
            <span
              className={`min-w-0 break-words font-semibold text-on-surface ${expanded ? '' : 'line-clamp-2'}`}
              title={a.patientName || 'Paciente'}
            >
              <Highlight text={a.patientName || 'Paciente'} term={searching ? trimmedSearch : ''} />
            </span>
            {patientAgeById[a.patientId] != null && (
              <span className="num-mono shrink-0 text-[11px] text-on-surface-variant">
                · {patientAgeById[a.patientId]}a
              </span>
            )}
          </p>
          <p className="flex min-w-0 items-baseline gap-2 text-[11px] text-on-surface-variant">
            <span className={`min-w-0 capitalize ${expanded ? 'break-words' : 'truncate'}`}>
              {a.type.toLowerCase().replace('_', ' ')}
              {a.isOverbooking && ' · encaixe'}
              {a.extraSlot && ' · fora das vagas'}
            </span>
            {waitMin != null && (
              <span className={`num-mono ml-auto flex shrink-0 items-center gap-1 ${waitText}`}>
                <UserCheck size={11} className="shrink-0" aria-hidden />
                {waitMin} min
              </span>
            )}
          </p>

          {/* Procedimentos lançados na visita. 2+: cada um no seu horário
              (início + i × slot). 1: só o nome. */}
          {hasProcedureDetail(a) && (() => {
            const names = procedureNames(a);
            if (names.length >= 2) {
              return (
                <div className="mt-0.5 leading-tight">
                  {names.map((name, i) => (
                    <p key={i} className={`flex items-center gap-1 text-[10px] ${expanded ? '' : 'truncate'}`}>
                      <span className="num-mono shrink-0 text-on-surface-variant/60">
                        {addMinutesToHM(a.startTime, i * slotMinutes)}
                      </span>
                      <span className={`font-semibold text-on-surface/80 ${expanded ? 'break-words' : 'truncate'}`}>
                        {name || 'Procedimento'}
                      </span>
                    </p>
                  ))}
                </div>
              );
            }
            return (
              <p className={`mt-0.5 text-[10px] font-semibold text-on-surface/80 ${expanded ? 'break-words' : 'truncate'}`}>
                {names[0] || 'Procedimento'}
              </p>
            );
          })()}

          {/* Selos — urgência / chamado / próximo / status / dilatação /
              convênio / cobrança / espera. Nenhum foi retirado; só ficaram
              menores e mais quietos. */}
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {a.priority === 'URGENCIA' && (
              <span className={`${badge} bg-error/15 text-error`}>
                <AlertCircle size={9} /> Urgência
              </span>
            )}
            {called && (
              <span className={`${badge} bg-sage/15 text-sage`}>
                <Volume2 size={9} /> Chamado{called.room ? ` · ${called.room}` : ''}
              </span>
            )}
            {!called && isNext && (
              <span className={`${badge} bg-amber/15 text-amber`}>
                <Star size={9} className={a.isNextUp ? 'fill-amber' : ''} /> Próximo
              </span>
            )}
            {showStatus && (
              <span className={`${badge} ${STATUS_TONE[a.status]}`}>{STATUS_LABEL[a.status]}</span>
            )}
            {/* Em dilatação — o médico pausou o atendimento e mandou o
                paciente dilatar a pupila (status volta pra EM_ESPERA +
                flag). Mesma linguagem âmbar do card da ficha. */}
            {a.status === 'EM_ESPERA' && a.attendancePausedForDilation && (
              <span
                className={`${badge} bg-amber/15 text-amber ring-1 ring-amber/40`}
                title="Paciente dilatando a pupila na sala de espera — o médico retoma o atendimento pela ficha"
              >
                <Droplets size={9} /> Em dilatação
              </span>
            )}
            {/* Convênio de HOJE — mesma lógica-fonte do selo da ficha
                (buildTodayInsurer): com insurerId → convênio; sem insurerId e
                insurerChoice='PARTICULAR' → Particular de propósito; sem
                escolha explícita (legado/portal) → alerta "Definir convênio"
                em vez de assumir Particular por omissão. */}
            {a.insurerId ? (
              <span
                className={`${badge} max-w-full bg-on-surface/10 text-on-surface`}
                title={`Convênio: ${insurerNameById[a.insurerId] ?? 'não identificado'}`}
              >
                <ShieldCheck size={9} className="shrink-0" />
                <span className={expanded ? 'break-words' : 'truncate'}>
                  {insurerNameById[a.insurerId] ?? 'Convênio'}
                </span>
              </span>
            ) : a.insurerChoice === 'PARTICULAR' ? (
              <span
                className={`${badge} bg-on-surface/5 text-on-surface-variant`}
                title="Particular (sem convênio)"
              >
                <ShieldCheck size={9} className="shrink-0" /> Particular
              </span>
            ) : (
              <span
                className={`${badge} bg-amber/15 text-amber ring-1 ring-amber/40`}
                title="Convênio ou particular do dia ainda não selecionado — abra o agendamento para definir."
              >
                <ShieldCheck size={9} className="shrink-0" /> Definir convênio
              </span>
            )}
            {/* Unidade (spec 019) — só com a Agenda em "Todas as unidades". */}
            {unitTagFor(a) && (
              <span className={`${badge} max-w-full bg-primary/10 text-primary`} title={`Unidade: ${unitTagFor(a)}`}>
                <MapPin size={9} className="shrink-0" />
                <span className={expanded ? 'break-words' : 'truncate'}>{unitTagFor(a)}</span>
              </span>
            )}
            {/* Cobrança — status de pagamento + valor. Espelha a coluna
                "Valor" da agenda: badge colorido por status, parcial mostra
                recebido/total. Sem cobrança lançada, mostra só o previsto. */}
            {charged && a.paymentStatus ? (
              hasOutstandingBalance(a) ? (
                <span
                  className={`${badge} ${PAYMENT_STATUS_TONE.PARCIAL}`}
                  title={`Parcial — recebido ${fmtMoneyBRL(amountReceived(a))} de ${fmtMoneyBRL(money ?? 0)} · falta ${fmtMoneyBRL(remainingBalance(a))}`}
                >
                  <DollarSign size={9} /> {fmtMoneyBRL(amountReceived(a))}/{fmtMoneyBRL(money ?? 0)}
                </span>
              ) : (
                <span className={`${badge} ${PAYMENT_STATUS_TONE[a.paymentStatus]}`}>
                  <DollarSign size={9} /> {PAYMENT_STATUS_LABEL[a.paymentStatus]}
                  {!isZeroCharge(a.paymentStatus) && money !== undefined ? ` · ${fmtMoneyBRL(money)}` : ''}
                </span>
              )
            ) : money !== undefined ? (
              <span
                className={`${badge} bg-surface-container-high font-semibold text-on-surface-variant`}
                title="Valor previsto — cobrança ainda não lançada"
              >
                <DollarSign size={9} /> {fmtMoneyBRL(money)}
              </span>
            ) : null}
            {/* Minutagem de espera — viva enquanto espera, congelada
                (chegada → início) após o atendimento. Em dilatação, conta
                desde o envio pro colírio (dilationPausedAt). Na sala de
                espera o número já aparece na linha do tipo, então aqui só
                entra fora dela. */}
            {waitMin == null && a.checkedInAt && a.status === 'EM_ESPERA' && (
              a.attendancePausedForDilation && a.dilationPausedAt ? (
                <span className={`${badge} num-mono bg-amber/10 font-normal normal-case tracking-normal text-amber`}>
                  dilatando há {formatWait(nowMs - a.dilationPausedAt)}
                </span>
              ) : (
                <span className={`${badge} num-mono bg-warning/10 font-normal normal-case tracking-normal text-warning`}>
                  esperando há {formatWait(nowMs - a.checkedInAt)}
                </span>
              )
            )}
            {a.checkedInAt && a.attendanceStartedAt && (
              <span className={`${badge} num-mono bg-on-surface/5 font-normal normal-case tracking-normal text-on-surface-variant`}>
                esperou {formatWait(a.attendanceStartedAt - a.checkedInAt)}
              </span>
            )}
            {renderRowActions(a, { markable, callable, called })}
          </div>

          {/* Detalhes extras só no modo ampliado — observações do
              agendamento e, se cancelado, o motivo. */}
          {expanded && a.notes && (
            <p className="mt-1.5 break-words text-[11px] text-on-surface-variant">
              <span className="font-semibold text-on-surface/70">Obs.:</span> {a.notes}
            </p>
          )}
          {expanded && cancelled && a.cancellationReason && (
            <p className="mt-1 break-words text-[11px] text-error/80">
              <span className="font-semibold">Motivo do cancelamento:</span> {a.cancellationReason}
            </p>
          )}
        </div>
      </div>
    );
  };

  /** Ações da linha: marcar próximo (estrela, MANUAL) + chamar com 1
   *  clique. Quietas por padrão, sempre acessíveis (não escondidas em hover —
   *  a gaveta também é usada em tela sensível ao toque). Ficam na linha dos
   *  SELOS, à direita — não numa coluna própria, que tirava ~65px de todas as
   *  linhas, inclusive a do nome. */
  const renderRowActions = (
    a: Appointment,
    { markable, callable, called }: { markable: boolean; callable: boolean; called: QueueEntry | undefined },
  ) => {
    if (!markable && !callable) return null;
    return (
      <div className="-my-1 ml-auto flex shrink-0 items-center gap-0.5">
        {markable && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); void handleToggleNext(a); }}
            disabled={markingId !== null}
            title={
              a.isNextUp
                ? 'Desmarcar como próximo a ser atendido'
                : `Marcar ${a.patientName || 'paciente'} como próximo a ser atendido`
            }
            aria-pressed={!!a.isNextUp}
            className={`${iconBtnClass} ${a.isNextUp ? 'bg-amber/15 text-amber hover:text-amber' : 'hover:text-amber'}`}
          >
            {markingId === a.id ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Star size={14} className={a.isNextUp ? 'fill-amber' : ''} />
            )}
          </button>
        )}
        {callable && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); void handleCall(a); }}
            disabled={callingId !== null}
            title={
              called
                ? `Chamar ${a.patientName || 'paciente'} novamente${called.room ? ` (${called.room})` : ''}`
                : `Chamar ${a.patientName || 'paciente'} agora`
            }
            className={`${iconBtnClass} ${called ? 'bg-sage/15 text-sage hover:text-sage' : 'hover:text-primary'}`}
          >
            {callingId === a.id ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Volume2 size={14} />
            )}
          </button>
        )}
      </div>
    );
  };

  /** Botão de escape da busca — rótulo longo quebra em duas linhas em vez de
   *  esticar a gaveta (o nome completo de um paciente cabe aqui). */
  const softButton = (
    label: ReactNode,
    onClick: () => void,
    icon: ReactNode,
    tone: 'blue' | 'quiet' = 'blue',
  ) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left text-[12.5px] font-medium leading-snug transition-colors
        ${tone === 'blue'
          ? 'border-primary/30 bg-primary/5 text-primary hover:bg-primary/10'
          : 'border-outline/15 text-on-surface-variant hover:bg-surface-container hover:text-on-surface'}`}
      style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="min-w-0" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
        {label}
      </span>
    </button>
  );

  /** Cartão AGORA — a leitura que domina a gaveta. */
  const renderLiveCard = (a: Appointment) => {
    const called = calledById[a.id];
    return (
      <div key={a.id} className={`${pad} pb-1`}>
        <div
          className={`relative rounded-xl border border-live/30 bg-surface-container p-3.5 pb-3 ${expanded ? 'max-w-[520px]' : ''}`}
          style={{ backgroundImage: 'linear-gradient(163deg, rgb(var(--c-live) / 0.10), transparent 70%)' }}
        >
          <span className="corner corner-tl corner-live-tl" aria-hidden />
          <span className="corner corner-tr corner-live-tr" aria-hidden />
          <span className="corner corner-bl corner-live-bl" aria-hidden />
          <span className="corner corner-br corner-live-br" aria-hidden />

          <div className="flex items-center justify-between gap-2.5">
            <span className="flex items-center gap-1.5 font-mono text-[9.5px] font-semibold uppercase tracking-[0.19em] text-live">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-live animate-live-pulse" aria-hidden />
              Em atendimento
            </span>
            {a.attendanceStartedAt ? (
              <Chrono
                startedAt={a.attendanceStartedAt}
                className="num-mono text-[21px] font-medium leading-none tracking-tight text-live"
              />
            ) : (
              <span className="num-mono text-[15px] text-live">em curso</span>
            )}
          </div>

          <p className="mb-1 mt-2 font-display text-[20px] font-medium leading-tight tracking-tight">
            {a.patientName || 'Paciente'}
          </p>

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-on-surface-variant">
            {!a.noTime && <span className="num-mono font-medium text-on-surface">{a.startTime}</span>}
            <span className="h-[2.5px] w-[2.5px] rounded-full bg-on-surface-variant/70" aria-hidden />
            <span className="capitalize">{a.type.toLowerCase().replace('_', ' ')}</span>
            {patientAgeById[a.patientId] != null && (
              <>
                <span className="h-[2.5px] w-[2.5px] rounded-full bg-on-surface-variant/70" aria-hidden />
                <span className="num-mono">{patientAgeById[a.patientId]}a</span>
              </>
            )}
            {called?.room && (
              <span className="rounded-full border border-outline/20 px-1.5 py-px font-mono text-[9px] uppercase tracking-wider">
                {called.room}
              </span>
            )}
          </div>

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => goToPatient(a.patientId)}
              className="flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg border border-outline/15 bg-surface-container-low text-[12.5px] font-medium transition-colors hover:bg-surface-container-high"
            >
              <FileText size={14} /> Abrir ficha
            </button>
            {!!user && (
              <button
                type="button"
                onClick={() => void handleCall(a)}
                disabled={callingId !== null}
                title={called ? `Chamar ${a.patientName || 'paciente'} novamente` : `Chamar ${a.patientName || 'paciente'}`}
                className={`flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-lg border px-3 text-[12.5px] font-medium transition-colors disabled:opacity-50
                  ${called ? 'border-sage/40 bg-sage/10 text-sage' : 'border-outline/15 bg-surface-container-low hover:bg-surface-container-high'}`}
              >
                {callingId === a.id ? <Loader2 size={14} className="animate-spin" /> : <Volume2 size={14} />}
                {called ? 'Rechamar' : 'Chamar'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /** Cartão PRÓXIMO. */
  const renderNextCard = () => {
    const a = nextAppt;

    return (
      <div className={`${pad} pb-1`}>
        <div
          ref={nextRowRef}
          className={`relative overflow-hidden rounded-xl border border-outline/15 bg-surface-container p-3 pl-4 ${expanded ? 'max-w-[520px]' : ''}`}
        >
          <span className="absolute bottom-2.5 left-0 top-2.5 w-[3px] rounded-r-sm bg-sage" aria-hidden />
          {a ? (
            <>
              <div className="flex items-center gap-2">
                <span className="num-mono text-[13px] font-medium">
                  {a.noTime ? 'A seguir' : a.startTime}
                </span>
                <span className="rounded-full border border-outline/20 px-1.5 py-px font-mono text-[9px] uppercase tracking-wider text-on-surface-variant">
                  {a.type.toLowerCase().replace('_', ' ')}
                </span>
                {/* SECRETÁRIA: o botão avisa o médico (pop-up na tela dele)
                    de QUAL paciente está entrando — vale a qualquer hora,
                    o paciente pode entrar adiantado. */}
                {user && role && role !== Role.MEDICO ? (
                  <>
                    {nextInMinutes != null && nextInMinutes > 0 && (
                      <span className="num-mono ml-auto text-[10px] font-semibold uppercase tracking-wider text-amber">
                        em {nextInMinutes} min
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => void notifyDoctor(a)}
                      disabled={doctorAlertSendingId !== null}
                      title={
                        doctorAlertSentId === a.id
                          ? `Médico já avisado — clique para avisar de novo que ${a.patientName || 'o paciente'} está entrando`
                          : `Avisar o médico, na tela dele, que ${a.patientName || 'o paciente'} está entrando`
                      }
                      className={`num-mono inline-flex h-7 items-center gap-1.5 rounded-lg border px-2.5 text-[10.5px] font-bold uppercase tracking-wider shadow-sm transition-colors active:scale-[0.97] disabled:opacity-60
                        ${nextInMinutes != null && nextInMinutes > 0 ? '' : 'ml-auto'}
                        ${doctorAlertSentId === a.id
                          ? 'border-sage/50 bg-sage/15 text-sage hover:bg-sage/25'
                          : 'border-amber/50 bg-amber/15 text-amber hover:bg-amber hover:text-white'}`}
                    >
                      {doctorAlertSendingId === a.id ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : doctorAlertSentId === a.id ? (
                        <Check size={13} />
                      ) : (
                        <DoorOpen size={13} />
                      )}
                      {doctorAlertSentId === a.id ? 'Médico avisado' : 'Avisar médico'}
                    </button>
                  </>
                ) : /* MÉDICO: na hora (ou atrasado), o selo vira o próprio
                    botão de chamar — mesma chamada do alto-falante da linha. */
                nextInMinutes != null && (nextInMinutes > 0 || !user ? (
                  <span className="num-mono ml-auto rounded-full border border-amber/30 bg-amber/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber">
                    {nextInMinutes > 0 ? `em ${nextInMinutes} min` : 'chamar agora'}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => void handleCall(a)}
                    disabled={callingId !== null}
                    title={
                      calledById[a.id]
                        ? `Chamar ${a.patientName || 'paciente'} novamente`
                        : `Chamar ${a.patientName || 'paciente'} agora`
                    }
                    className="num-mono ml-auto inline-flex h-7 items-center gap-1.5 rounded-lg border border-amber/50 bg-amber/15 px-2.5 text-[10.5px] font-bold uppercase tracking-wider text-amber shadow-sm transition-colors hover:bg-amber hover:text-white active:scale-[0.97] disabled:opacity-60"
                  >
                    {callingId === a.id ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <Volume2 size={13} />
                    )}
                    {calledById[a.id] ? 'Rechamar' : 'Chamar agora'}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => goToPatient(a.patientId)}
                className="mt-1.5 block max-w-full text-left font-display text-[17px] font-medium leading-tight tracking-tight hover:underline"
                title="Abrir ficha do paciente"
              >
                {a.patientName || 'Paciente'}
              </button>
              {/* Chegou ou ainda vem? Vira PÍLULA (cheia × tracejada), o
                  mesmo par de sinais da grade da agenda — é o dado que
                  decide se dá pra chamar o próximo agora. */}
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11.5px] text-on-surface-variant">
                {a.isOverbooking && <span>Encaixe ·</span>}
                {a.extraSlot && <span>Fora das vagas ·</span>}
                {a.checkedInAt ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-sage/50 bg-sage/15 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-sage">
                    <UserCheck size={9} className="shrink-0" aria-hidden />
                    NA CLÍNICA · CHEGOU {clockOf(a.checkedInAt)}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-outline/40 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-on-surface-variant/70">
                    <span className="inline-block h-1.5 w-1.5 rounded-full border border-current" aria-hidden />
                    NÃO CHEGOU
                  </span>
                )}
                {a.isNextUp && <span>· marcado na mão</span>}
              </div>
            </>
          ) : (
            <>
              <p className="font-display text-[17px] font-medium leading-tight">Ninguém na fila</p>
              <p className="mt-0.5 text-[11.5px] text-on-surface-variant">
                Nenhum paciente aguardando — o próximo aparece aqui assim que alguém
                chegar ou for marcado na estrela.
              </p>
            </>
          )}
        </div>

      </div>
    );
  };

  /** CHAT na largura do painel, logo abaixo da busca. Antes era um ícone de
   *  28px espremido no cabeçalho, entre o pin e o ampliar — difícil de achar
   *  e fácil de errar o clique: é comando de uso constante do painel. Some
   *  quando o chat não está encaixado aqui (aí ele flutua e tem o próprio
   *  balão). Não lidas aparecem no próprio botão, em vermelho. */
  const chatUnread = views.chat?.badge ?? 0;
  const chatButton = views.chat ? (
    <button
      type="button"
      onClick={() => openView(view === 'chat' ? 'agenda' : 'chat')}
      aria-label={view === 'chat' ? 'Voltar para a agenda' : 'Abrir o chat'}
      aria-pressed={view === 'chat'}
      title={view === 'chat'
        ? 'Voltar para a agenda do dia (Alt+A)'
        : `Chat da clínica (Alt+C)${chatUnread > 0 ? ` — ${chatUnread} não lida(s)` : ''}`}
      className={`flex h-8 w-full items-center justify-center gap-1.5 rounded-lg border border-dashed text-[12.5px] font-medium transition-colors
        ${view === 'chat'
          ? 'border-primary/50 bg-primary/15 text-primary'
          : chatUnread > 0
            ? 'border-error/40 bg-error/5 text-error hover:bg-error/10'
            : 'border-outline/25 text-on-surface-variant hover:bg-surface-container hover:text-on-surface'}`}
    >
      {view === 'chat' ? (
        <><CalendarDays size={14} /> Voltar para a agenda</>
      ) : (
        <>
          <MessageSquare size={14} /> Chat
          {chatUnread > 0 && (
            <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-error px-1 text-[9px] font-bold leading-none text-on-error">
              {chatUnread > 9 ? '9+' : chatUnread}
            </span>
          )}
        </>
      )}
    </button>
  ) : null;

  /** O chat fica FIXO no topo do painel (não rola com a lista) e aparece
   *  também nas outras vistas do dock (chat, resumo…) — de lá ele é a volta
   *  pra agenda. O "Mandar entrar" saiu do painel em 09/10/2026: mora na
   *  faixa de atendimento da ficha ([[AttendanceTimerCard]]), sempre à
   *  mostra, e na barra de baixo da Agenda. */
  const chatBar = chatButton ? (
    <div className={`shrink-0 border-b border-outline/10 py-2 ${pad}`}>
      {chatButton}
    </div>
  ) : null;

  /** Estado VAZIO — horários abertos, o que vem, quem cabe hoje e o que não
   *  depende da agenda. Nunca "Nenhum agendamento para hoje." e ponto. */
  const renderEmpty = () => {
    const enabled = !!todayWindow?.enabled;
    const start = todayWindow?.start ?? '';
    const end = todayWindow?.end ?? '';
    // Marcas de hora do trilho: de 2 em 2 horas dentro da janela.
    const marks: number[] = [];
    if (enabled && start && end) {
      const s = Math.ceil(hmToMinutes(start) / 60);
      const e = Math.floor(hmToMinutes(end) / 60);
      for (let h = s; h <= e; h += 2) marks.push(h);
    }
    const span = enabled ? hmToMinutes(end) - hmToMinutes(start) : 0;

    return (
      <>
        <div className={`${pad} pt-3`}>
          <div className="rounded-xl border border-outline/15 bg-surface-container p-3.5">
            <div className="mb-3 flex items-baseline gap-2.5">
              <span className="num-mono text-[24px] font-medium leading-none tracking-tight">
                {enabled ? openSlots : '—'}
              </span>
              <span className="text-[11.5px] leading-snug text-on-surface-variant">
                {enabled ? 'horários abertos' : 'hoje não é dia de atendimento'}
                <br />
                {enabled ? (
                  <>janela de <b className="num-mono font-semibold text-on-surface">{start}</b> às{' '}
                    <b className="num-mono font-semibold text-on-surface">{end}</b></>
                ) : (
                  <>sem expediente configurado para hoje</>
                )}
              </span>
            </div>

            {enabled && span > 0 && (
              <div className="relative pb-4">
                <div
                  className="relative h-7 overflow-hidden rounded-md border border-outline/15"
                  style={{
                    backgroundImage:
                      'repeating-linear-gradient(115deg, rgb(var(--c-outline) / 0.14) 0 5px, transparent 5px 11px)',
                  }}
                  aria-hidden
                >
                  {marks.slice(1, -1).map((h) => (
                    <span
                      key={h}
                      className="absolute bottom-0 top-0 w-px bg-outline/20"
                      style={{ left: `${((h * 60 - hmToMinutes(start)) / span) * 100}%` }}
                    />
                  ))}
                </div>
                <div className="relative h-0" aria-hidden>
                  {marks.map((h, i) => {
                    const p = ((h * 60 - hmToMinutes(start)) / span) * 100;
                    const style: CSSProperties =
                      i === 0
                        ? { left: 0 }
                        : i === marks.length - 1
                          ? { right: 0 }
                          : { left: `${p}%`, transform: 'translateX(-50%)' };
                    return (
                      <span
                        key={h}
                        className="num-mono absolute top-1 text-[9px] text-on-surface-variant/70"
                        style={style}
                      >
                        {String(h).padStart(2, '0')}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="mt-3 text-[12.5px] leading-relaxed text-on-surface-variant">
              Nenhum paciente marcado — a agenda está{' '}
              <b className="font-semibold text-on-surface">aberta</b>, não vazia por erro.
            </p>

            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => goToAgenda()}
                className="flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg bg-gradient-cta text-[12.5px] font-semibold text-on-primary-container shadow-sm transition-all hover:shadow-ambient"
              >
                <UserPlus size={14} /> Marcar alguém para hoje
              </button>
            </div>
          </div>
        </div>

        {upcomingDays.length > 0 && (
          <>
            <Sec label="O que vem" count={upcomingDays.length} pad={pad} />
            {upcomingDays.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => goToAgenda(d.key)}
                className={`group flex w-full items-center gap-2.5 py-2 text-left transition-colors hover:bg-surface-container/60 ${pad}`}
              >
                <span className="h-[23px] w-[3px] shrink-0 rounded-sm bg-sage/80" aria-hidden />
                <span className="w-[52px] shrink-0">
                  <span className="label-mono block leading-none">{d.weekday}</span>
                  <span className="num-mono mt-1 block text-[12.5px] font-semibold">{d.label}</span>
                </span>
                <span className="min-w-0 flex-1 text-[12.5px]">
                  {d.count} agendamento{d.count === 1 ? '' : 's'}
                  <small className="num-mono mt-px block text-[10.5px] text-on-surface-variant">
                    {d.first} → {d.last}
                    {d.unconfirmed > 0 ? ` · ${d.unconfirmed} a confirmar` : ''}
                  </small>
                </span>
                <ArrowRight size={14} className="shrink-0 text-on-surface-variant opacity-0 transition-opacity group-hover:opacity-100" />
              </button>
            ))}
          </>
        )}

        {couldComeToday.length > 0 && (
          <>
            <Sec label="Cabem hoje" count={couldComeToday.length} pad={pad} />
            <div className={`${pad} flex flex-col items-start gap-1.5`}>
              {couldComeToday.map(({ a, when }) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => goToPatient(a.patientId)}
                  className="inline-flex max-w-full items-center gap-2 rounded-full border border-outline/15 bg-surface-container px-2.5 py-1.5 text-[12.5px] transition-colors hover:border-outline/30 hover:bg-surface-container-high"
                >
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sage" aria-hidden />
                  <span className="truncate">{a.patientName || 'Paciente'}</span>
                  <span className="num-mono shrink-0 text-[10.5px] text-on-surface-variant">{when}</span>
                </button>
              ))}
            </div>
            <p className={`${pad} mt-2 text-[11px] leading-relaxed text-on-surface-variant`}>
              Marcados nos próximos {UPCOMING_DAYS} dias e ainda sem confirmação —
              cabem no consultório livre de hoje.
            </p>
          </>
        )}

        <Sec label="Não depende da agenda" pad={pad} />
        <EmptyDayPendencias
          clinicId={clinicId}
          role={role}
          onOpen={() => { closeDock(); navigate('/pendencias'); }}
        />
      </>
    );
  };

  // ── Trilho de ícones (borda direita, sempre visível no desktop) ──────
  // Em cima, as vistas do painel (Agenda · Chat · Resumo · Optotipos — as
  // três últimas só quando a página as registrou); no meio, os mostradores
  // vivos do dia; embaixo, atendidos/total. O conteúdo reserva a largura do
  // trilho, então ele nunca cobre nada.
  const railTile = 'flex w-10 flex-col items-center justify-center gap-0.5 rounded-lg py-1.5 transition-transform hover:-translate-y-px';
  // O chat sai do cinza do trilho e usa o AZUL do chat (`primary`, a mesma cor
  // do balão flutuante) — é o ícone que a equipe precisa achar de relance.
  const railViewBtn = (v: DockView, icon: ReactNode, hint?: string) => {
    const active = dockOpen && view === v;
    const badge = views[v]?.badge ?? 0;
    const label = views[v]?.label ?? VIEW_LABEL[v];
    const isChat = v === 'chat';
    return (
      <button
        type="button"
        onClick={() => onRailClick(v)}
        title={hint ? `${label} — ${hint}` : label}
        aria-label={active ? `Recolher ${label.toLowerCase()}` : `Abrir ${label.toLowerCase()}`}
        aria-pressed={active}
        className={`relative grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors ${
          active
            ? isChat
              ? 'bg-primary/15 text-primary'
              : 'bg-amber/15 text-amber'
            : isChat
              ? 'text-primary hover:bg-primary/10'
              : 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'
        }`}
      >
        {icon}
        {badge > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-error px-1 text-[9px] font-bold leading-none text-on-error">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </button>
    );
  };
  const rail = (
    <div
      ref={railRef}
      data-dock-root
      className="fixed right-0 top-0 z-50 hidden h-full w-[52px] flex-col items-center gap-1.5 border-l border-outline/15 bg-surface-container-low py-2.5 sm:flex"
    >
      {/* Zonas da AGENDA no trilho (o ícone e os mostradores do dia, abaixo)
          — parar o mouse nelas faz a agenda espiar (sanfona). Os ícones das
          outras vistas ficam fora: passar no chat não abre a agenda. */}
      <div onPointerEnter={peekEnter} onPointerLeave={peekLeave} onPointerDownCapture={clearPeekTimer}>
        {railViewBtn('agenda', <CalendarDays size={18} />, 'Alt+A abre, amplia e recolhe')}
      </div>
      {views.chat && railViewBtn('chat', <MessageSquare size={18} />, 'Alt+C')}
      {views.resumo && railViewBtn('resumo', <Activity size={18} />, 'Alt+R')}
      {views.optotipos && railViewBtn('optotipos', <Eye size={18} />, 'controla a tela do paciente')}
      {views.paciente && railViewBtn('paciente', <UserCheck size={18} />, 'marcação do paciente selecionado')}
      <span className="my-1 h-px w-6 bg-outline/15" aria-hidden />

      <div
        className="flex w-full min-h-0 flex-1 flex-col items-center gap-1.5"
        onPointerEnter={peekEnter}
        onPointerLeave={peekLeave}
        // Clicou antes da espiada abrir → é a abertura normal; a espiada
        // pendente não pode chegar depois e desencaixar o painel.
        onPointerDownCapture={clearPeekTimer}
      >
      {liveMinutes != null && (
        <button
          type="button"
          onClick={toggleDrawer}
          title="Atendimento em andamento"
          className={`${railTile} border border-live/30 bg-live/10`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-live animate-live-pulse" aria-hidden />
          <span className="num-mono text-[12.5px] font-semibold leading-none text-live">{liveMinutes}′</span>
        </button>
      )}
      {nextAppt && (
        <button
          type="button"
          onClick={toggleDrawer}
          title={`Próximo: ${nextAppt.patientName || 'paciente'}${nextAppt.noTime ? '' : ` às ${nextAppt.startTime}`}`}
          className={`${railTile} border border-amber/30 bg-amber/10`}
        >
          <span className="font-mono text-[7.5px] uppercase tracking-[0.14em] text-on-surface-variant">Próx</span>
          <span className="num-mono text-[12.5px] font-semibold leading-none text-amber">
            {nextInMinutes == null ? '—' : nextInMinutes > 0 ? `${nextInMinutes}′` : 'já'}
          </span>
        </button>
      )}

      {spine.length > 0 && (
        <>
          <span className="h-px w-6 bg-outline/15" aria-hidden />
          <div className="flex max-h-40 flex-col items-center gap-[3px] overflow-hidden" title="A forma do dia" aria-hidden>
            {spine.slice(0, 16).map((s, i) => (
              <span
                key={i}
                className={`block rounded-sm ${
                  s === 'now' ? 'h-3.5 w-[7px] bg-live'
                    : s === 'next' ? 'h-3 w-[5px] bg-amber'
                      : s === 'done' ? 'h-3 w-[5px] bg-on-surface-variant/60'
                        : s === 'miss' ? 'h-3 w-[5px] bg-error/50'
                          : 'h-3 w-[5px] bg-on-surface-variant/20'
                }`}
              />
            ))}
          </div>
        </>
      )}

      <button
        type="button"
        onClick={toggleDrawer}
        title={`Agenda do dia — ${counts.done} de ${counts.total} atendidos`}
        className="num-mono mt-auto flex flex-col items-center gap-0.5 rounded-md px-1 py-1 transition-colors hover:bg-surface-container-high"
      >
        <span className="text-[14px] font-semibold leading-none">{String(counts.done).padStart(2, '0')}</span>
        <span className="h-px w-4 bg-outline/25" aria-hidden />
        <span className="text-[10px] leading-none text-on-surface-variant">{String(counts.total).padStart(2, '0')}</span>
      </button>
      </div>
    </div>
  );

  const mobileTab = (
    <button
      type="button"
      onClick={toggleDrawer}
      title="Agenda do dia"
      aria-label="Abrir agenda do dia"
      className="fixed right-0 top-1/2 z-50 flex -translate-y-1/2 flex-col items-center gap-1 rounded-l-xl bg-primary px-1.5 py-3 text-on-primary shadow-lg transition-all hover:brightness-110 sm:hidden"
    >
      <ChevronLeft size={16} />
      <CalendarDays size={18} />
      <span className="text-[10px] font-bold uppercase tracking-wider [writing-mode:vertical-rl] rotate-180">
        Agenda
      </span>
    </button>
  );

  return (
    <>
      {railShown && rail}
      {!dockOpen && mobileTab}

      {panelShown && (
        /* Sem backdrop de propósito: a gaveta age como painel lateral
           não-modal — a tela de fundo continua visível E clicável. Recolhe
           pelo trilho, X, Esc, Alt+A OU ao clicar fora dela. No desktop
           encosta à esquerda do trilho; no celular é tela cheia.
           As vistas que não são a agenda (chat, resumo, optotipos)
           renderizam o próprio conteúdo via portal no <div> do slot. */
        <aside
          ref={asideRef}
          data-dock-root
          role="dialog"
          aria-label={VIEW_LABEL[view]}
          style={{ '--agenda-w': panelWidthCss } as CSSProperties}
          onPointerEnter={() => { peekHover.current = true; clearPeekTimer(); }}
          onPointerLeave={peekLeave}
          className={`fixed right-0 top-0 z-50 flex h-full w-full flex-col border-l border-outline/20 bg-surface-container-low transition-[width] duration-300 ease-out animate-slide-in-right sm:right-[52px] sm:w-[var(--agenda-w)] ${
            peek ? 'shadow-[-18px_0_44px_-14px_rgb(0_0_0/0.4)]' : 'shadow-ambient'
          }`}
        >
          {/* ── Cabeçalho ── (fica FORA do switch de vistas: o topo — dia,
              contadores e busca — segue à mostra mesmo
              quando o painel troca pro chat, resumo ou optotipos) */}
          <div className={`shrink-0 border-b border-outline/10 pb-3 pt-3.5 ${pad}`}>
            <div className="mb-1.5 flex items-center gap-1">
              <span className="label-mono flex min-w-0 flex-1 items-center gap-1.5">
                <CalendarDays size={12} /> Agenda do dia
              </span>
              {/* Só no desktop: no celular o "manter aberta" não age (ver
                  pinnedHere) e trocá-lo dali mudaria os computadores da conta. */}
              <button
                type="button"
                onClick={togglePinned}
                aria-pressed={pinned}
                aria-label="Manter sempre aberta"
                title={pinned ? 'Manter sempre aberta — ativado. Clique para desafixar.' : 'Manter sempre aberta'}
                className={`hidden h-7 w-7 shrink-0 place-items-center rounded-md transition-colors sm:grid ${
                  pinned ? 'bg-amber/15 text-amber' : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
                }`}
              >
                <Pin size={15} className={pinned ? 'fill-amber' : ''} />
              </button>
              {/* Alterna entre o tamanho padrão e o ampliado (agenda inteira,
                  2 colunas, sem truncar). Só no desktop — no mobile o painel
                  já é tela cheia. */}
              <button
                type="button"
                onClick={toggleExpanded}
                className={`h-7 w-7 shrink-0 place-items-center rounded-md text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface ${
                  view === 'agenda' ? 'hidden sm:grid' : 'hidden'
                }`}
                title={expanded ? 'Voltar ao tamanho parcial' : 'Ampliar para ver a agenda completa'}
                aria-label={expanded ? 'Reduzir painel ao tamanho parcial' : 'Ampliar painel da agenda'}
                aria-pressed={expanded}
              >
                {expanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface"
                aria-label="Fechar painel"
                title="Recolher a gaveta (Esc)"
              >
                <X size={16} />
              </button>
            </div>

            {/* first-letter em vez de `capitalize`: em pt-BR o dia vem
                "sexta-feira, 29 de agosto" — capitalizar cada palavra viraria
                "29 De Agosto". */}
            <h2 className="text-[19px] font-semibold leading-tight tracking-tight first-letter:uppercase">
              {todayLabel}
            </h2>

            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11.5px] text-on-surface-variant">
              {counts.total === 0 ? (
                <>
                  <span>Sem atendimentos marcados</span>
                  <span className="h-[2.5px] w-[2.5px] rounded-full bg-on-surface-variant/70" aria-hidden />
                  <span>consultório livre o dia todo</span>
                </>
              ) : (
                <>
                  <b className="num-mono font-semibold text-on-surface">{String(counts.done).padStart(2, '0')}</b>
                  <span>de</span>
                  <b className="num-mono font-semibold text-on-surface">{String(counts.total).padStart(2, '0')}</b>
                  <span>atendidos</span>
                  {counts.lastEnd && (
                    <>
                      <span className="h-[2.5px] w-[2.5px] rounded-full bg-on-surface-variant/70" aria-hidden />
                      <span>término previsto</span>
                      <b className="num-mono font-semibold text-on-surface">{counts.lastEnd}</b>
                    </>
                  )}
                </>
              )}
            </div>

            <DayRangesStrip reserved={todayRanges.reserved} blocked={todayRanges.blocked} className="mt-2" dense />

            {/* Espinha do dia — atendidos, o que corre agora e o que falta. */}
            {spine.length > 0 && (
              <div
                className="mt-3 flex h-[7px] items-end gap-[3px]"
                title="A forma do dia: atendidos, em curso e o que falta"
                aria-hidden
              >
                {spine.map((s, i) => (
                  <span
                    key={i}
                    className={`flex-1 rounded-sm transition-transform ${
                      s === 'now' ? 'h-[7px] bg-live'
                        : s === 'next' ? 'h-[5px] bg-amber'
                          : s === 'done' ? 'h-[5px] bg-on-surface-variant/60'
                            : s === 'miss' ? 'h-[5px] bg-error/50'
                              : 'h-[5px] bg-on-surface-variant/20'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Busca única + escopo ── */}
          <div className={`flex shrink-0 items-center gap-2 border-b border-outline/10 py-2.5 ${pad}`}>
            <div className="min-w-0 flex-1">
              {dayOnly ? (
                <div className="relative">
                  <Search
                    size={14}
                    className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar paciente do dia…"
                    aria-label="Buscar paciente agendado do dia"
                    autoFocus={view === 'agenda'}
                    className="h-[34px] w-full rounded-lg border border-outline/15 bg-surface-container px-8 text-[13px] text-on-surface placeholder:text-on-surface-variant focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      className="absolute right-2 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface"
                      aria-label="Limpar busca"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              ) : (
                <GlobalPatientSearch key={globalSeed} initialQuery={globalSeed} />
              )}
            </div>
            <button
              type="button"
              onClick={() => (dayOnly ? widenScope(trimmedSearch) : narrowScope())}
              aria-pressed={dayOnly}
              title={
                dayOnly
                  ? 'Buscando só nos agendados de hoje — clique pra abrir a base inteira'
                  : 'Buscando em todos os pacientes — clique pra voltar ao dia'
              }
              className={`h-[34px] shrink-0 rounded-lg border px-2.5 font-mono text-[10px] font-medium uppercase tracking-wider transition-colors ${
                dayOnly
                  ? 'border-amber/30 bg-amber/10 text-amber'
                  : 'border-outline/15 text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
              }`}
            >
              Do dia
            </button>
          </div>

          {chatBar}

          {view !== 'agenda' ? (
            <div ref={setSlotEl} className="flex min-h-0 flex-1 flex-col" />
          ) : (
          <>
          {/* ── Miolo rolável (cockpit fixo: só o miolo rola) ── */}
          <div ref={listRef} className="custom-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain pb-4">
            {callError && (
              <div className={`mt-2.5 flex items-center gap-2 rounded-lg bg-error-container/30 p-2 text-xs text-on-error-container ${pad}`}>
                <AlertCircle size={14} className="shrink-0" />
                <span className="flex-1">{callError}</span>
                <button
                  type="button"
                  onClick={() => setCallError(null)}
                  className="text-on-error-container/70 hover:text-on-error-container"
                  aria-label="Fechar aviso"
                >
                  <X size={12} />
                </button>
              </div>
            )}

            {loading && appts.length === 0 ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-on-surface-variant">
                <Loader2 size={16} className="animate-spin" /> Carregando…
              </div>
            ) : appts.length === 0 ? (
              renderEmpty()
            ) : (
              <>
                {/* ── AGORA ── */}
                {!searching && liveAppts.length > 0 && (
                  <>
                    <Sec label="Agora" pad={pad} />
                    {liveAppts.map(renderLiveCard)}
                  </>
                )}

                {/* ── PRÓXIMO ── */}
                {!searching && (nextAppt || role === Role.MEDICO) && (
                  <>
                    <Sec label="Próximo" pad={pad} />
                    {renderNextCard()}
                  </>
                )}

                {/* ── SALA DE ESPERA ── */}
                {sections.waiting.length > 0 && (
                  <>
                    <Sec label="Sala de espera" count={sections.waiting.length} pad={pad} />
                    <div className={gridCols}>
                      {sections.waiting.map((a) => (
                        <Fragment key={a.id}>{renderRow(a, { showStatus: false, showWaitClock: true })}</Fragment>
                      ))}
                    </div>
                    {worstWait && (
                      <p className={`${pad} mt-1.5 text-[11px] leading-relaxed text-on-surface-variant`}>
                        {worstWait.min >= WAIT_TARGET_MIN ? (
                          <>
                            {firstName(worstWait.a.patientName)} chegou às{' '}
                            <b className="num-mono font-semibold">{clockOf(worstWait.a.checkedInAt ?? 0)}</b> e
                            espera há <b className="font-semibold text-terracotta">{worstWait.min} min</b> — passou dos{' '}
                            {WAIT_TARGET_MIN} min de meta da clínica.
                          </>
                        ) : (
                          <>
                            Ninguém passou dos {WAIT_TARGET_MIN} min de meta da clínica — a maior
                            espera é de <b className="font-semibold text-on-surface">{worstWait.min} min</b>.
                          </>
                        )}
                      </p>
                    )}
                  </>
                )}

                {/* ── RESTO DO DIA ── */}
                {sections.rest.length > 0 && (
                  <>
                    <Sec
                      label={searching ? 'Encontrados no dia' : 'Resto do dia'}
                      count={sections.rest.length}
                      pad={pad}
                    />
                    <div className={gridCols}>
                      {sections.rest.map((a, idx) => {
                        // Divisória "Tarde · 13h" antes do 1º paciente da
                        // tarde (só quando há manhã antes dele).
                        const firstPmIdx = sections.rest.findIndex((x) => x.startTime >= shiftBoundary);
                        const showNoonDivider = idx === firstPmIdx && firstPmIdx > 0;
                        return (
                          <Fragment key={a.id}>
                            {showNoonDivider && (
                              <div className={`col-span-full flex items-center gap-2 pb-1 pt-2.5 ${pad}`} aria-hidden>
                                <span className="h-px flex-1 bg-outline/15" />
                                <span className="flex items-center gap-1 font-mono text-[9px] font-semibold uppercase tracking-wider text-on-surface-variant">
                                  <Sun size={11} className="opacity-70" /> Tarde · {shiftLabel(shiftBoundary)}
                                </span>
                                <span className="h-px flex-1 bg-outline/15" />
                              </div>
                            )}
                            {renderRow(a)}
                          </Fragment>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* ── ENCERRADOS (recolhível) ── */}
                {sections.closed.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowClosed((v) => !v)}
                      aria-expanded={showClosed}
                      className={`flex w-full items-center gap-2.5 pb-1.5 pt-4 text-left text-on-surface-variant transition-colors hover:text-on-surface ${pad}`}
                    >
                      {showClosed ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      <span className="label-mono">
                        {String(sections.closed.length).padStart(2, '0')} encerrados
                      </span>
                      <span className="h-px flex-1 bg-outline/10" />
                    </button>
                    {showClosed && (
                      <div className={gridCols}>
                        {sections.closed.map((a) => (
                          <Fragment key={a.id}>{renderRow(a)}</Fragment>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* ── Busca sem resultado / saída pra base inteira ── */}
                {searching && (
                  <div className={`${pad} mt-4`}>
                    {filteredAppts.length === 0 && (
                      <p className="mb-2 text-[12.5px] leading-relaxed text-on-surface-variant" style={{ overflowWrap: 'anywhere' }}>
                        Ninguém com{' '}
                        <b className="font-semibold text-on-surface">“{shortTerm(trimmedSearch)}”</b>{' '}
                        na agenda de hoje.
                      </p>
                    )}
                    {softButton(
                      <>Procurar “{shortTerm(trimmedSearch)}” em todos os pacientes</>,
                      () => widenScope(trimmedSearch),
                      <Search size={14} />,
                    )}
                    <p className="mt-1.5 text-center text-[11px] leading-relaxed text-on-surface-variant">
                      A busca do dia nunca é um beco: daqui você cai na base inteira.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Rodapé ── */}
          <div className={`shrink-0 space-y-2 border-t border-outline/10 py-2.5 ${pad}`}>
            {/* Resumo financeiro do dia — recebido / a receber / total.
                Os três fecham entre si e batem com a agenda completa. */}
            {financ.count > 0 && (
              <div className="rounded-lg border border-outline/15 bg-surface-container p-2.5">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="label-mono flex items-center gap-1">
                    <DollarSign size={11} /> Financeiro do dia
                  </span>
                  <span className="text-[10px] text-on-surface-variant">
                    {financ.count} cobrança{financ.count === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5 text-center">
                  <div>
                    <p className="label-mono">Recebido</p>
                    <p className="num-mono text-xs font-bold text-sage">{fmtMoneyBRL(financ.recebido)}</p>
                  </div>
                  <div>
                    <p className="label-mono">A receber</p>
                    <p className="num-mono text-xs font-bold text-warning">{fmtMoneyBRL(financ.aReceber)}</p>
                  </div>
                  <div>
                    <p className="label-mono">Total</p>
                    <p className="num-mono text-xs font-bold text-primary">{fmtMoneyBRL(financ.total)}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              {/* Já na agenda completa, o atalho pra ela não tem pra onde
                  levar — some em vez de virar um botão que não faz nada. */}
              {!onAgendaPage && (
                <button
                  type="button"
                  onClick={() => goToAgenda()}
                  className="-ml-1.5 inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[12px] font-medium text-primary transition-colors hover:bg-primary/10"
                >
                  <Clock size={13} /> Ver agenda completa
                  <ExternalLink size={11} className="opacity-70" />
                </button>
              )}
              <span className="ml-auto flex items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-wider text-on-surface-variant">
                <span className="h-[5px] w-[5px] rounded-full bg-sage" aria-hidden /> Ao vivo
              </span>
            </div>
          </div>
          </>
          )}
        </aside>
      )}

      {/* Alerta de acompanhante ao marcar como próximo — pergunta se o
          paciente entra acompanhado e grava o acompanhante do dia no
          agendamento. Depois do <aside> pra empilhar por cima (mesmo z-50).
          O listener em tempo real da gaveta já reflete a gravação. */}
      <NextUpAccompanistModal
        isOpen={accompanistAsk !== null}
        clinicId={clinicId}
        appointment={accompanistAsk}
        onClose={() => setAccompanistAsk(null)}
      />
      {notifyDoctorModal}
    </>
  );
};
